/* LAV TDMS — MDR & VDR registers (master rail → documents table). */
import { type CSSProperties, type ReactNode, useState, useEffect, useMemo, useRef } from 'react'
import type { CodingDoc, CodingOther, Doc, Mdr, RegisterStatus, Vdoc, Vdr, Vendor } from '@/data/types'
import { qk, useFacets, useItem, useList, useSub, inboxKeys } from '@/data/queries'
import { useCommand } from '@/data/mutations'
import type { DocAttrDef } from '@/data/meta'
import { useCoding, useContracts, useDictLabels, useDictRows, useDocAttrs, useMe } from '@/data/meta'
import { fetchList, noteExport, requestFile, saveFile } from '@/data/api'
import { parseDocs, sampleRow, toDocRow, unknownColumns, type DocRow } from '@/lib/excel'
import { TrmFilesZone, uploadTrmFile } from '@/screens/pt/TrmFiles'
import { today } from '@/lib/format'
import {
  Button,
  Badge,
  CodeChip,
  DateCell,
  DateInput,
  PageHead,
  Purpose,
  LifeBadge,
  DiscTag,
  Crit,
  EmptyState,
  Toolbar,
  ToolSpacer,
  CompletionBar,
  Field,
  SmartTable,
  Spinner,
  Input,
  Select,
  DictPicker,
  toast,
  ResizeHandles,
  useResizable,
  useNarrow,
  ResizableRail,
} from '@/ui'
import type { CellValue, SmartColumn } from '@/ui'
import { Icon } from '@/ds/icons'
import { colHeader } from '@/i18n/columns'
import { t, type Lang } from '@/i18n/lang'
import { intersectFilters, sliceFilters, type Slice } from './slices'



const cmpDate = (a?: string | null, b?: string | null): number => {
  if (!a || a === '—') return b && b !== '—' ? -1 : 0
  if (!b || b === '—') return 1
  return a < b ? -1 : a > b ? 1 : 0
}

// ---- Shared documents table -------------------------------------------
export function DocsTable({
  name,
  resource = 'docs',
  rows,
  lang,
  onOpenDoc,
  onOpenViewer,
  selected,
  setSelected,
  extra,
  loading,
  attrs,
  sort,
  onSort,
  colFilters,
  onFilter,
  emptyText,
}: {
  /** Имя раскладки: у реестра MDR и у реестра VDR она своя, столбцы общие. */
  name?: string
  /** Чей это состав: по нему спрашиваются значения отбора у сервера. */
  resource?: 'docs' | 'vdocs'
  rows: (Doc | Vdoc)[]
  lang: Lang
  onOpenDoc: (no: string) => void
  /** Открыть чертёж. Без него значок файла остаётся просто признаком. */
  onOpenViewer?: (no: string) => void
  selected?: Set<React.Key>
  setSelected?: (s: Set<React.Key>) => void
  extra?: string
  /** Данные ещё идут: таблица показывает заглушку своей высоты, а не строку текста. */
  loading?: boolean
  /** Проектные колонки реестра и их значения по номерам документов. */
  attrs?: { defs: DocAttrDef[]; values: Record<string, Record<string, string>> }
  /* Порядок держит экран, а считает сервер: заголовок сортировал загруженную
     страницу — пятьдесят строк из полутора тысяч, — и выглядел настоящим. */
  sort?: { key: string; dir: 'asc' | 'desc' } | null
  onSort?: (s: { key: string; dir: 'asc' | 'desc' } | null) => void
  /* Отбор в шапке — тоже запрос: галочки отбирали загруженную страницу, и
     реестр отвечал «на рассмотрении три документа» там, где их полторы сотни. */
  colFilters?: Record<string, string[]>
  onFilter?: (key: string, values: string[] | null) => void
  /* Чем объяснить пустую таблицу. По умолчанию «нет документов по заданным
     фильтрам» — но у пустого заказа фильтров нет ни одного, и человек начинал
     искать несуществующее условие вместо того, чтобы наполнить заказ. */
  emptyText?: ReactNode
}) {
  // Подписи статусов — из того же справочника, что редактирует администратор,
  // а не из копии словаря в этом файле.
  const lifeLabels = useDictLabels('life')
  /* Значения отбора берутся из справочников, а не из загруженной страницы:
     сервер отдаёт отобранное, и собранный по странице список схлопывался бы до
     уже выбранного значения — отбор нечем было бы расширить. */
  const purposeValues = Object.keys(useDictLabels('purpose'))
  const codeValues = Object.keys(useDictLabels('code'))
  const titulRows = useDictRows('titul')
  const areaRows = useDictRows('area')
  /* Значения проектных колонок известны только данным: у атрибута-текста
     справочника нет вовсе. Их отдаёт сервер счётчиками — тем же путём, каким
     считает отбор, поэтому список и числа сходятся с выдачей. */
  const attrKeys = useMemo(() => (attrs?.defs ?? []).map((d) => 'attr.' + d.code), [attrs])
  const attrFacets = useFacets(resource, attrKeys)
  const attrValues = (code: string): string[] => {
    const buckets = attrFacets.data?.['attr.' + code]
    return buckets ? buckets.map((b) => String(b.value)) : []
  }
  const attrCounts = (code: string): Record<string, number> | undefined => {
    const buckets = attrFacets.data?.['attr.' + code]
    return buckets ? Object.fromEntries(buckets.map((b) => [String(b.value), b.count])) : undefined
  }
  const dateCell = (v: CellValue, kind: string, hint?: string) =>
    <DateCell value={v == null ? '' : String(v)} kind={kind} title={hint} />
  /* Срок ТЕКУЩЕЙ стадии документа, а не первой.
     Сроки ведутся по стадиям круга (IFR → IFA → AFD), а колонки «План/Прогноз/
     Факт» показывали общие поля документа — они совпадают со сроком первой
     стадии. Документ, ушедший на IFA, продолжал показывать сроки IFR: колонка
     говорила про круг, который уже прошёл.
     Стадии нет среди трёх (документ выпущен как ASB, CAN и подобное) или срок
     по ней не задан — остаётся общее поле: оно и есть срок документа. */
  const STAGE_DATES: Record<string, { plan: keyof Doc; forecast: keyof Doc; fact: keyof Doc }> = {
    IFR: { plan: 'planIfr', forecast: 'forecastIfr', fact: 'factIfr' },
    IFA: { plan: 'planIfa', forecast: 'forecastIfa', fact: 'factIfa' },
    AFD: { plan: 'planAfd', forecast: 'forecastAfd', fact: 'factAfd' },
  }
  const stageDate = (r: Doc | Vdoc, kind: 'plan' | 'forecast' | 'fact'): { value: string; stage: string | null } => {
    const keys = STAGE_DATES[String(r.purpose)]
    /* Документ на стадии круга показывает СВОЙ срок — и пустоту, если его не
       назначили. Подставлять сюда общее поле нельзя: оно осталось от прошлой
       стадии, и колонка снова говорила бы про пройденный круг. Пустая клетка
       честнее: срок стадии не назначен, и это видно. */
    if (keys) {
      const own = (r as unknown as Record<string, string | null | undefined>)[keys[kind]]
      return { value: own ?? '', stage: String(r.purpose) }
    }
    /* Назначение вне круга (ASB, CAN, IFI…) стадийных сроков не имеет: у такого
       документа срок один — его собственный. */
    const common = (r as unknown as Record<string, string | null | undefined>)[kind]
    return { value: common ?? '', stage: null }
  }
  /* Подсказка называет стадию, к которой относится дата: заголовок колонки её
     назвать не может — у каждой строки стадия своя. */
  const stageHint = (stage: string | null, kind: string, value: string) =>
    stage ? stage + ' · ' + kind + (value ? '' : t(lang, ': срок не назначен', ': no date set')) : undefined
  const cols: SmartColumn<Doc | Vdoc>[] = [
    {
      key: 'no', header: colHeader(lang, '№ документа'), mono: true, nowrap: true, width: 152, required: true, render: (r) =>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Crit on={'crit' in r && r.crit} /><span style={{ color: 'var(--text-strong)', fontWeight: 500 }}>{r.no}</span></span>,
    },
    {
      key: 'title', header: colHeader(lang, 'Наименование'), render: (r) =>
        <div style={{ minWidth: 0 }}>
          <div style={{ color: 'var(--text-body)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 320 }}>{r.title}</div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 1 }}>{r.type} · {r.vendor}</div>
        </div>,
    },
    /* Титул и объект строки: с V90 это её свойства, а не свойства реестра —
       реестр по контракту идёт по нескольким объектам, и в его составе объект
       строки надо видеть. У документа поставщика объекта нет: в его витрине
       такой колонки нет вовсе, и прочерк означает именно это. */
    { key: 'titul', header: colHeader(lang, 'Титул'), mono: true, width: 64, groupable: true, filterable: true,
      filterValues: titulRows.map((r) => r.code),
      valueLabel: (v) => { const row = titulRows.find((x) => x.code === v); return row ? row.code + ' — ' + row.name : String(v || '—') },
      render: (r) => <span style={{ color: r.titul ? 'var(--violet-600)' : 'var(--text-subtle)', fontWeight: 600, fontSize: 11 }}>{r.titul || '—'}</span> },
    { key: 'area', header: colHeader(lang, 'Объект'), mono: true, width: 68, groupable: true, filterable: true,
      filterValues: areaRows.map((r) => r.code),
      valueLabel: (v) => { const row = areaRows.find((x) => x.code === v); return row ? row.code + ' — ' + row.name : String(v || '—') },
      render: (r) => { const area = 'area' in r ? r.area : ''; return <span style={{ color: area ? 'var(--text-body)' : 'var(--text-subtle)', fontSize: 11 }}>{area || '—'}</span> } },
    ...(extra === 'mdrRow' ? [{ key: 'mdrRow', header: colHeader(lang, 'Строка MDR'), mono: true, nowrap: true, width: 130, render: (r) => ('mdrRow' in r && r.mdrRow) ? <span style={{ color: 'var(--text-link)' }}>{'mdrRow' in r ? r.mdrRow : ''}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span> } as SmartColumn<Doc | Vdoc>] : []),
    {
      key: 'rev', header: colHeader(lang, 'Рев.'), align: 'center', width: 64, render: (r) =>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, justifyContent: 'center' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: r.rev === '—' ? 'var(--text-subtle)' : 'var(--text-strong)' }}>{r.rev}</span>
          {r.ver > 1 ? <span title={t(lang, 'версий: ', 'versions: ') + r.ver} style={{ fontFamily: 'var(--font-mono)', fontSize: 9.5, color: 'var(--text-subtle)', background: 'var(--surface-sunken)', border: '1px solid var(--border-subtle)', borderRadius: 2, padding: '0 3px' }}>v{r.ver}</span> : null}
        </span>,
    },
    { key: 'purpose', header: colHeader(lang, 'Назн.'), width: 64, filterable: true, filterValues: purposeValues, valueLabel: (v) => v || '—', render: (r) => <Purpose value={r.purpose} size="sm" /> },
    { key: 'code', header: colHeader(lang, 'Код'), align: 'center', width: 50, filterable: true, filterValues: codeValues, valueLabel: (v) => v || '—', render: (r) => <CodeChip code={r.code} size="sm" /> },
    { key: 'life', header: colHeader(lang, 'Статус'), width: 150, groupable: true, filterable: true, filterValues: Object.keys(lifeLabels), valueLabel: (v) => lifeLabels[String(v)] ?? String(v), render: (r) => <LifeBadge life={r.life} /> },
    {
      key: 'plan', header: colHeader(lang, 'План'), mono: true, align: 'right', width: 84,
      /* Отбор и сортировка идут по общему полю: их считает сервер, и подменять
         значение здесь значило бы сортировать одно, а показывать другое. */
      render: (r) => { const d = stageDate(r, 'plan'); return dateCell(d.value, 'planned', stageHint(d.stage, t(lang, 'план', 'plan'), d.value)) },
    },
    {
      key: 'forecast', header: colHeader(lang, 'Прогноз'), mono: true, align: 'right', width: 84,
      render: (r) => {
        const d = stageDate(r, 'forecast')
        const plan = stageDate(r, 'plan')
        const fact = stageDate(r, 'fact')
        return dateCell(d.value, cmpDate(d.value, plan.value) > 0 && !fact.value ? 'overdue' : 'forecast',
          stageHint(d.stage, t(lang, 'прогноз', 'forecast'), d.value))
      },
    },
    {
      key: 'fact', header: colHeader(lang, 'Факт'), mono: true, align: 'right', width: 84,
      render: (r) => { const d = stageDate(r, 'fact'); return dateCell(d.value, 'actual', stageHint(d.stage, t(lang, 'факт', 'actual'), d.value)) },
    },
    { key: 'trm', header: 'TRM', mono: true, align: 'right', width: 86, filterable: true, valueLabel: (v) => v || '—', render: (r) => r.trm ? <span style={{ color: 'var(--text-link)' }}>{r.trm}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span> },
    /* Есть ли основной файл. Значок кликабельный: по нему открывается сам
       чертёж, а не карточка — иначе до файла три нажатия вместо одного. */
    {
      key: 'hasMain', header: colHeader(lang, 'Файл'), align: 'center', width: 56, render: (r) => (
        <button type="button" className="tool-btn"
              title={r.hasMain
          ? t(lang, 'Основной файл загружен — открыть', 'Main file uploaded — open')
          : t(lang, 'Основного файла нет', 'No main file')}
              disabled={!r.hasMain || !onOpenViewer}
              onClick={(e) => { if (r.hasMain && onOpenViewer) { e.stopPropagation(); onOpenViewer(r.no) } }}
              style={{ display: 'inline-flex', border: 0, padding: 0, background: 'transparent', color: r.hasMain ? 'var(--success-ink)' : 'var(--text-subtle)', cursor: r.hasMain && onOpenViewer ? 'pointer' : 'default' }}>
          <Icon name={r.hasMain ? 'file-text' : 'square-dashed'} size={14} />
        </button>
      ),
    },
    { key: 'comments', header: colHeader(lang, 'Зам.'), align: 'center', width: 52, render: (r) => r.comments ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: 'var(--danger-ink)', fontFamily: 'var(--font-mono)', fontSize: 11 }}><Icon name="message-square" size={12} />{r.comments}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span> },
    /* Отклонение от базового (контрактного) плана. Базовый срок ставит
       согласование реестра и дальше не двигает: без него сдвиг сравнивался с
       ПОСЛЕДНИМ согласованием, и после каждого изменения система отвечала
       «различий нет», пока документ уезжал от контракта на месяцы. */
    {
      key: 'planDrift', header: colHeader(lang, 'Отклонение, дн.'), mono: true, align: 'right', width: 108, render: (r) =>
        r.planDrift == null
          ? <span style={{ color: 'var(--text-subtle)' }}>—</span>
          : <span style={{ color: r.planDrift > 0 ? 'var(--danger-ink)' : 'var(--text-body)' }}>{r.planDrift > 0 ? '+' + r.planDrift : r.planDrift}</span>,
    },
    /* Проектные колонки — тег, система, гриф, формат — идут в хвост своими
       столбцами. Прежде они жили только в карточке и в файле выгрузки: реестр
       заказчика ведут по ним, а увидеть их в реестре было нельзя — сортировать
       и группировать тем более. Раскладка запоминается, как у прочих колонок:
       кому они не нужны, тот их скроет. */
    ...(attrs?.defs ?? []).map((d) => ({
      key: 'attr.' + d.code,
      header: colHeader(lang, d.title),
      width: 120,
      nowrap: true,
      groupable: d.kind === 'list',
      /* Отбор по проектной колонке идёт ЗАПРОСОМ: ключ колонки — «attr.<код>»,
         и сервер понимает его тем же полем, что и панель фильтров над таблицей
         (f.attr.system=21). Раньше отбор был закрыт вовсе — колонку показали, а
         отобрать по ней было нечем, кроме отдельной панели. */
      filterable: true,
      filterValues: attrValues(d.code),
      filterCounts: attrCounts(d.code),
      value: (r: Doc | Vdoc) => attrs?.values[r.no]?.[d.code] ?? '',
      render: (r: Doc | Vdoc) => {
        const v = attrs?.values[r.no]?.[d.code]
        return v ? <span style={{ color: 'var(--text-body)' }}>{v}</span> : <span style={{ color: 'var(--text-subtle)' }}>—</span>
      },
    } as SmartColumn<Doc | Vdoc>)),
  ]

  const keyOf = (r: Doc | Vdoc) => r.no
  const sel = selected || new Set<React.Key>()
  return (
    <SmartTable
      name={name}
      columns={cols}
      rows={rows}
      rowKey={keyOf}
      lang={lang}
      selectedKeys={setSelected ? sel : undefined}
      onToggleRow={setSelected ? (k) => { const n = new Set(sel); if (n.has(k)) n.delete(k); else n.add(k); setSelected(n) } : undefined}
      onToggleAll={setSelected ? (all) => setSelected(all ? new Set(rows.map(keyOf)) : new Set()) : undefined}
      onRowClick={(r) => onOpenDoc(r.no)}
      sort={sort}
      onSort={onSort}
      colFilters={colFilters}
      onFilter={onFilter}
      loading={loading}
      emptyText={emptyText ?? t(lang, 'Нет документов по заданным фильтрам', 'No documents match the filters')} />)


}

// ---- Master rail item --------------------------------------------------
/** Состояния, в которых состав реестра правится. Зеркало сервера. */
const FILLABLE: readonly RegisterStatus[] = ['draft', 'rework', 'amending']
export const isFillable = (status?: RegisterStatus): boolean => !!status && FILLABLE.includes(status)

/* Подписи состояний реестра. Экспортируются: карточка реестра показывала
   машинный код («rework»), а он адресован коду, не человеку. */
export const STATUS_LABEL: Record<RegisterStatus, [string, string]> = {
  draft: ['Черновик', 'Draft'],
  submitted: ['Отправлен', 'Submitted'],
  review: ['На рассмотрении', 'In review'],
  rework: ['На доработке', 'Rework'],
  approved: ['Согласован', 'Approved'],
  amending: ['Изменяется', 'Amending'],
  closed: ['Закрыт', 'Closed'],
}

const STATUS_TONE: Record<RegisterStatus, 'neutral' | 'info' | 'warning' | 'success' | 'accent'> = {
  draft: 'neutral', submitted: 'info', review: 'info', rework: 'warning',
  approved: 'success', amending: 'accent', closed: 'neutral',
}

function StatusBadge({ status, rev, lang }: { status?: RegisterStatus; rev?: number; lang: Lang }) {
  if (!status) return null
  const [ru, en] = STATUS_LABEL[status]
  return <Badge tone={STATUS_TONE[status]} size="sm">{t(lang, ru, en)}{rev && status === 'approved' ? ' · ' + t(lang, 'ред. ', 'rev ') + rev : ''}</Badge>
}

function RailItem({ active, onClick, code, id, title, sub, status, rev, lang, afd, ifa, ifr, planned, overdue }: {
  active: boolean
  onClick: () => void
  /** Дисциплина реестра. У реестра на контракт её нет — ярлык не рисуется. */
  code?: string
  id: string
  title: string
  sub?: string
  status?: RegisterStatus
  rev?: number
  lang: Lang
  afd: number
  ifa: number
  ifr: number
  planned: number
  overdue: number
}) {
  return (
    <button onClick={onClick} className="tdms-rail" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: active ? 'var(--accent-soft)' : 'transparent', boxShadow: active ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        {code ? <DiscTag code={code} /> : null}
        {/* Номер не ломается по дефису: «MDR-CV-1200» вставало тремя строками. */}
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, whiteSpace: 'nowrap', color: active ? 'var(--accent-ink)' : 'var(--text-subtle)' }}>{id}</span>
        <StatusBadge status={status} rev={rev} lang={lang} />
        {overdue > 0 ? <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 3, fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--danger-ink)' }}>● {overdue}</span> : null}
      </div>
      <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-strong)', fontWeight: active ? 600 : 400, marginTop: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
      {sub ? <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sub}</div> : null}
      <div style={{ marginTop: 7 }}><CompletionBar afd={afd} ifa={ifa} ifr={ifr} planned={planned} height={5} /></div>
    </button>)


}

function FilterControls({ lang, q, setQ, disc, setDisc, life, setLife, slice, setSlice, attrFilter, attrActive, views, onExport, onTemplate, onAddDoc }: {
  lang: Lang
  q: string
  setQ: (v: string) => void
  disc: string
  setDisc: (v: string) => void
  life: string
  setLife: (v: string) => void
  slice: Slice
  setSlice: (v: Slice) => void
  /** Отбор по проектной колонке — своим блоком: набор колонок у каждого свой. */
  attrFilter?: ReactNode
  /** Включён ли отбор по проектной колонке: счётчик на кнопке «Фильтры». */
  attrActive?: boolean
  /** Сохранённые фильтры — своим блоком, чтобы у обоих реестров он был один. */
  views?: ReactNode
  onExport?: () => void
  /** Пустая книга того же вида: её заполняет подрядчик и присылает обратно. */
  onTemplate?: () => void
  onAddDoc?: () => void
}) {
  /* Значения фильтров берутся из тех же справочников, что и сами данные.
     Раньше список приходил из seed, а выбранное значение уходило на сервер:
     любое расхождение молча давало пустую выдачу. */
  const discRows = useDictRows('disc')
  const lifeRows = useDictRows('life')
  /* На ноутбуке эта строка не помещается: поиск, три отбора, отбор по проектной
     колонке, сохранённые фильтры и кнопки — около 1660 px содержимого при 800
     доступных. Панель переносилась на три ряда и отнимала у таблицы 84 px
     высоты ровно там, где нужны строки реестра.

     Ниже порога всё, кроме поиска и действий, уезжает под кнопку «Фильтры» и
     раскрывается по требованию. Не поповером: панель стоит в потоке, а слой
     поверх пришлось бы позиционировать над таблицей, закрывать по клику вне и
     держать в границах карточки с overflow. Раскрывающаяся строка делает то же
     самое и ничего не накрывает.

     Сколько отборов включено — видно на кнопке: спрятанный отбор, о котором не
     сказали, — это неверный реестр без объяснения. */
  const narrow = useNarrow()
  const [filtersOpen, setFiltersOpen] = useState(false)
  const activeCount = [disc, life, slice].filter(Boolean).length + (attrActive ? 1 : 0)
  const filters = (
    <>
      <Select size="sm" value={disc} onChange={(e) => setDisc(e.target.value)} block={false} style={{ width: 160 }}>
        <option value="">{t(lang, 'Все дисциплины', 'All disciplines')}</option>
        {discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}
      </Select>
      <Select size="sm" value={life} onChange={(e) => setLife(e.target.value)} block={false} style={{ width: 170 }}>
        <option value="">{t(lang, 'Любой статус', 'Any status')}</option>
        {lifeRows.map((r) => <option key={r.code} value={r.code}>{r.name}</option>)}
      </Select>
      {/* Срез — вопрос, который задают каждый день: что просрочено, что сдаём
          на неделе, где нет файла или кода. Раньше это собиралось руками, а
          «просрочено» не набиралось вовсе: точным совпадением по дате его не
          выразить. */}
      <Select size="sm" value={slice} onChange={(e) => setSlice(e.target.value as Slice)} block={false} style={{ width: 180 }}>
        <option value="">{t(lang, 'Весь реестр', 'Whole register')}</option>
        <option value="overdue">{t(lang, 'Просрочено', 'Overdue')}</option>
        <option value="week">{t(lang, 'Срок на неделе', 'Due this week')}</option>
        <option value="ifa-week">{t(lang, 'На согласование за неделю', 'To approval this week')}</option>
        <option value="afd-week">{t(lang, 'В работу (AFD) за неделю', 'To AFD this week')}</option>
        <option value="behind">{t(lang, 'Отстаёт от базового плана', 'Behind baseline')}</option>
        <option value="nofile">{t(lang, 'Без файла', 'No file')}</option>
        <option value="nocode">{t(lang, 'Без кода', 'No code')}</option>
      </Select>
      {attrFilter}
      {views}
    </>
  )
  return (
    <>
      <Toolbar>
        <div style={{ width: narrow ? 200 : 240 }}><Input size="sm" value={q} onChange={(e) => setQ(e.target.value)} leading={<Icon name="search" size={14} />} placeholder={t(lang, 'Поиск по № или наименованию…', 'Search…')} /></div>
        {narrow ? (
          <Button variant={filtersOpen ? 'secondary' : 'ghost'} size="sm" aria-expanded={filtersOpen}
                  iconLeft={<Icon name="list-filter" size={14} />} onClick={() => setFiltersOpen(!filtersOpen)}>
            {t(lang, 'Фильтры', 'Filters')}{activeCount ? ' · ' + activeCount : ''}
          </Button>
        ) : filters}
        <ToolSpacer />
        {onAddDoc ? <Button variant="primary" size="sm" iconLeft={<Icon name="file-plus" size={14} />} onClick={onAddDoc}>{t(lang, 'Создать документ', 'New document')}</Button> : null}
        <Button variant="secondary" size="sm" iconLeft={<Icon name="download" size={14} />} onClick={onExport}>Excel</Button>
          {/* Шаблон — та же книга без строк: со списками справочников и с
              правилами реестра в заголовке. Выгрузить его, не выпуская
              трансмиттал, прежде было нечем. */}
          {onTemplate ? <Button variant="secondary" size="sm" iconLeft={<Icon name="file-down" size={14} />}
                                title={t(lang, 'Пустая книга реестра со списками справочников', 'An empty register book with dictionary lists')}
                                onClick={onTemplate}>{t(lang, 'Шаблон', 'Template')}</Button> : null}
      </Toolbar>
      {narrow && filtersOpen ? <Toolbar>{filters}</Toolbar> : null}
    </>)


}

/* Отбор по проектной колонке — запросом, а не по загруженной странице.

   Значения предлагаются счётчиками фасетов: это те значения, которые в реестре
   действительно встречаются, и считает их сервер по всему набору. Пока колонка
   не выбрана, запрос за значениями не идёт. */
function AttrFilter({ lang, resource, defs, pick, onPick }: {
  lang: Lang
  resource: 'docs' | 'vdocs'
  defs: DocAttrDef[]
  pick: { code: string; value: string }
  onPick: (p: { code: string; value: string }) => void
}) {
  const facet = useSub<Record<string, { value: string; count: number }[]>>(
    pick.code ? `/facets/${resource}?by=${encodeURIComponent('attr.' + pick.code)}` : null)
  if (!defs.length) return null
  const values = facet.data?.['attr.' + pick.code] ?? []
  return (
    <>
      <Select size="sm" value={pick.code} onChange={(e) => onPick({ code: e.target.value, value: '' })} block={false} style={{ width: 170 }}>
        <option value="">{t(lang, 'Любая колонка', 'Any column')}</option>
        {defs.map((d) => <option key={d.code} value={d.code}>{d.title}</option>)}
      </Select>
      {pick.code ? (
        <Select size="sm" value={pick.value} onChange={(e) => onPick({ code: pick.code, value: e.target.value })} block={false} style={{ width: 170 }}>
          <option value="">{t(lang, 'Любое значение', 'Any value')}</option>
          {values.map((b) => <option key={b.value} value={b.value}>{b.value} · {b.count}</option>)}
        </Select>
      ) : null}
    </>)
}

/* Сохранённые фильтры реестра.

   Набор условий, которым человек пользуется каждый день, собирался заново при
   каждом входе. Хранится он на сервере, а не в браузере: тот же человек
   работает и с другой машины. Что именно вернётся по фильтру, решает обычная
   выборка со всеми её ограничениями — фильтр ничего не открывает. */
type ViewState = { q: string; disc: string; life: string; slice: Slice }

function SavedFilters({ lang, resource, current, apply }: {
  lang: Lang
  resource: 'docs' | 'vdocs'
  current: ViewState
  apply: (v: ViewState) => void
}) {
  const path = `/views?resource=${resource}`
  const views = useSub<{ rows: { id: number; name: string; query: ViewState }[] }>(path)
  const rows = views.data?.rows ?? []
  const [chosen, setChosen] = useState('')
  const [nameOpen, setNameOpen] = useState(false)
  const [name, setName] = useState('')

  const save = useCommand<{ name: string; query: ViewState }>({
    method: 'PUT',
    path: () => '/views',
    body: (a) => ({ resource, name: a.name, query: a.query }),
    invalidate: () => [qk.sub(path)],
  })
  const drop = useCommand<{ id: number }>({
    method: 'DELETE',
    path: (a) => `/views/${a.id}`,
    invalidate: () => [qk.sub(path)],
  })

  const pick = (id: string) => {
    setChosen(id)
    const found = rows.find((r) => String(r.id) === id)
    if (found) apply(found.query)
  }

  const store = () => {
    const title = name.trim()
    if (!title) return
    save.mutate({ name: title, query: current }, {
      onSuccess: () => { setNameOpen(false); setName('') },
      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сохранилось', 'Not saved'), text: e.message }),
    })
  }

  return (
    <>
      <Select size="sm" value={chosen} onChange={(e) => pick(e.target.value)} block={false} style={{ width: 170 }}
        title={t(lang, 'Сохранённые фильтры', 'Saved filters')}>
        <option value="">{t(lang, 'Мои фильтры', 'My filters')}</option>
        {rows.map((r) => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
      </Select>
      <Button variant="ghost" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setNameOpen(true)}
        title={t(lang, 'Сохранить текущий отбор', 'Save current filter')}>{t(lang, 'Сохранить', 'Save')}</Button>
      {chosen ? (
        <Button variant="ghost" size="sm" iconLeft={<Icon name="trash-2" size={14} />} disabled={drop.isPending}
          onClick={() => drop.mutate({ id: Number(chosen) }, { onSuccess: () => setChosen('') })}
          title={t(lang, 'Удалить фильтр', 'Delete filter')}>{t(lang, 'Удалить', 'Delete')}</Button>
      ) : null}
      <DlgShell open={nameOpen} onClose={() => setNameOpen(false)} sizeKey="save-view" width={420}
        title={t(lang, 'Сохранить отбор', 'Save filter')}
        subtitle={t(lang, 'Название под тем же именем перезапишет прежний отбор', 'Saving under the same name overwrites it')}
        footer={<>
          <Button variant="secondary" size="sm" onClick={() => setNameOpen(false)}>{t(lang, 'Отмена', 'Cancel')}</Button>
          <Button variant="primary" size="sm" disabled={!name.trim() || save.isPending} onClick={store}>{t(lang, 'Сохранить', 'Save')}</Button>
        </>}>
        <Field label={t(lang, 'Название', 'Name')}>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t(lang, 'Например: просрочено по PI', 'e.g. overdue in PI')} />
        </Field>
      </DlgShell>
    </>)
}

/* onCreate отсутствует, пока не выбран заказ: кнопка отключается с пояснением,
   а не молчит. Пока команда идёт, кнопка занята — второе нажатие выпустило бы
   второй трансмиттал на те же документы. */
function BulkBar({ count, total, lang, onClear, onSelectAll, onCreate, onEdit, onVoid, busy, createHint }: { count: number; total?: number; lang: Lang; onClear: () => void; onSelectAll?: () => void; onCreate?: () => void; onEdit?: () => void; onVoid?: () => void; busy?: boolean; createHint?: string }) {
  if (!count) return null
  /* Галочка в шапке отмечает СТРАНИЦУ — двадцать пять строк. Реестр из двух
     тысяч строк, отфильтрованный до трёхсот просроченных, так не обработать:
     четырнадцать страниц и четырнадцать одинаковых правок. */
  const more = total != null && total > count
  return (
    <div className="bulkbar" style={{ position: 'sticky', bottom: 0, display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', background: 'var(--slate-900)', color: 'var(--slate-100)', borderTop: '2px solid var(--accent)' }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{count} {t(lang, 'выбрано', 'selected')}</span>
      {more && onSelectAll ? (
        <button onClick={onSelectAll} disabled={busy} className="tool-btn bulkbar-all"
                style={{ border: 0, background: 'transparent', color: 'var(--accent)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
          {t(lang, `Выбрать все ${total} по фильтру`, `Select all ${total} matching the filter`)}
        </button>
      ) : null}
      {/* Оттенок фиксированный, не --accent-hover: фон бара тёмный в обеих
          темах, а accent-hover в светлой теме темнеет и гасил бы ссылку.
          :not(:disabled) — занятая кнопка не притворяется живой. */}
      <style>{`@media (hover: hover) and (pointer: fine) { .bulkbar-all:not(:disabled):hover { color: var(--amber-300) !important; } }`}</style>
      <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
        <Button variant="dark" size="sm" iconLeft={<Icon name="x" size={13} />} onClick={onClear}>{t(lang, 'Снять', 'Clear')}</Button>
        {/* Сдвинуть срок сотне строк — работа реестра, а не сотня одинаковых
            правок карточек. */}
        {onEdit ? <Button variant="dark" size="sm" iconLeft={<Icon name="pencil" size={13} />} onClick={onEdit} disabled={busy}>{t(lang, 'Изменить выбранные', 'Edit selected')}</Button> : null}
        {/* Из реестра выпадает раздел, а не строка: объём сузили. Строки не
            исчезают — они помечаются аннулированными и остаются в истории. */}
        {onVoid ? <Button variant="dark" size="sm" iconLeft={<Icon name="trash-2" size={13} />} onClick={onVoid} disabled={busy}>{t(lang, 'Аннулировать', 'Void selected')}</Button> : null}
        {/* Причина, по которой кнопка гаснет, у реестров разная: у VDR не выбран
            заказ, у MDR у реестра не назван подрядчик. Одна подсказка на оба
            врала половине экранов. */}
        <Button variant="primary" size="sm" iconLeft={<Icon name="send" size={14} />} onClick={onCreate} disabled={!onCreate || busy}
                title={onCreate ? undefined : (createHint ?? t(lang, 'Заказ не выбран', 'No purchase order selected'))}>{busy ? t(lang, 'Формируем…', 'Creating…') : t(lang, 'Сформировать TRM', 'Create TRM')}</Button>
      </div>
    </div>)


}

// ---- Local dialog shell (faithful to prototype DS.Dialog: width/subtitle/footer)
function DlgShell({ open, onClose, title, subtitle, sizeKey, width = 520, footer, children, style = {} }: {
  open: boolean
  onClose: () => void
  title?: ReactNode
  subtitle?: ReactNode
  /** Под каким ключом запоминается размер окна между открытиями. */
  sizeKey: string
  width?: number
  footer?: ReactNode
  children?: ReactNode
  style?: CSSProperties
}) {
  const box = useResizable({ sizeKey, width, edges: ['left', 'right', 'bottom', 'bottom-left', 'bottom-right'] })
  /* Уход анимируется: open=false не размонтирует окно сразу, а включает фазу
     закрытия — .dlg-shell[data-closing] (ui.css) проигрывает вход задом
     наперёд, DOM снимается по animationend. Таймаут — страховка: при
     reduced-motion или отменённой анимации события может не быть, и без него
     полупрозрачное окно висело бы навсегда. Escape и клик по подложке идут
     этим же путём: оба зовут onClose, а фазу ведёт open. wasOpen отличает
     закрытие от первого рендера закрытым — иначе окно мигало бы уходом,
     ни разу не открывшись. */
  const [closing, setClosing] = useState(false)
  const wasOpen = useRef(false)
  useEffect(() => {
    if (open) { wasOpen.current = true; setClosing(false); return }
    if (!wasOpen.current) return
    wasOpen.current = false
    setClosing(true)
    const tm = window.setTimeout(() => setClosing(false), 200)
    return () => window.clearTimeout(tm)
  }, [open])
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && onClose) onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])
  if (!open && !closing) return null
  return (
    <div role="presentation" className="dlg-shell" data-closing={!open || undefined}
         onAnimationEnd={() => { if (!open) setClosing(false) }}
         onMouseDown={(e) => { if (e.target === e.currentTarget && onClose) onClose() }}
         /* Уходящее окно — уже не цель для указателя: без pointerEvents
            подложка-призрак ловила бы клик, предназначенный странице. */
         style={{ position: 'fixed', inset: 0, zIndex: 'var(--z-modal)' as CSSProperties['zIndex'], display: 'grid', placeItems: 'center', padding: 'var(--space-16)', background: 'rgba(14,17,22,0.45)', backdropFilter: 'blur(1px)', pointerEvents: !open ? 'none' : undefined }}>
      {/* Ограничение высоты считается вместе с отступами подложки: подложка во
          весь экран с padding var(--space-16), и 90vh внутри неё давали 90vh + 64 —
          на экране 768 окно вылезало нижней кромкой. Вызывающий стиль разлит ДО
          ограничений: своё оформление он задавать вправе, а снести границы
          экрана — нет. */}
      <div ref={box.ref} role="dialog" aria-modal="true" style={{ position: 'relative', width: box.width, height: box.height ?? undefined, display: 'flex', flexDirection: 'column', background: 'var(--surface-raised)', border: 'var(--border-hairline) solid var(--border-default)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden', ...style, maxWidth: 'calc(100vw - var(--space-16) * 2)', maxHeight: 'calc(100vh - var(--space-16) * 2)' }}>
        <ResizeHandles box={box} />
        {title ? (
          <header style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-6)', padding: 'var(--space-8) var(--space-10)', borderBottom: 'var(--border-hairline) solid var(--border-subtle)' }}>
            <div style={{ minWidth: 0 }}>
              <h2 style={{ font: 'var(--font-view-title)', color: 'var(--text-strong)' }}>{title}</h2>
              {subtitle ? <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{subtitle}</div> : null}
            </div>
            <button onClick={onClose} aria-label="Закрыть" className="icon-btn" style={{ marginLeft: 'auto', display: 'grid', placeItems: 'center', width: 28, height: 28, border: 0, background: 'transparent', color: 'var(--text-subtle)', cursor: 'pointer', fontSize: '16px', borderRadius: 'var(--radius-sm)' }}>✕</button>
          </header>
        ) : null}
        <div style={{ padding: 'var(--space-10)', overflow: 'auto', font: 'var(--font-body)', fontSize: 'var(--text-md)', color: 'var(--text-body)' }}>{children}</div>
        {footer ? (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-4)', padding: 'var(--space-8) var(--space-10)', borderTop: 'var(--border-hairline) solid var(--border-subtle)', background: 'var(--surface-sunken)' }}>{footer}</footer>
        ) : null}
      </div>
    </div>)


}

/* Диалоги создания монтируются только открытыми (форма сбрасывается
   размонтированием, справочники не грузятся зря) — но тогда родитель снимал
   компонент раньше, чем DlgShell успевал проиграть уход, и окно исчезало
   рывком. Хук держит mount ровно на фазу закрытия; 200 мс согласованы со
   страховочным таймаутом DlgShell. */
function useExitMount(open: boolean): boolean {
  const [mounted, setMounted] = useState(open)
  useEffect(() => {
    if (open) { setMounted(true); return }
    const tm = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(tm)
  }, [open])
  return open || mounted
}

/* Код реестра по его маске. Повторяет сборку сервера (DocumentNumber.plan) для
   тех источников, которые у реестра MDR есть: постоянная часть и контракт.
   Маски без сегментов у старых проектов не бывает — там остаётся прежний вид. */
export function assembleMdrCode(mask: CodingOther | undefined, contract: string): string {
  const segments = mask?.segments ?? []
  if (!segments.length) return 'MDR-' + contract
  const sep = mask?.sep ?? '-'
  return segments
    .map((s) => (s.srcKind === 'contract' ? contract : s.srcKind === 'const' ? (s.value ?? s.example) : s.example))
    .filter((part) => part !== '')
    .join(sep)
}

// ---- MDR creation dialog -----------------------------------------------
/* Метка «без контракта» в выпадающем списке. Пустая строка не годится: пустое
   состояние Select уже означает «первый свободный контракт». */
const OWN = '#own'

function MdrCreate({ open, onClose, lang, onCreated }: { open: boolean; onClose: () => void; lang: Lang; onCreated: (id: string) => void }) {
  // Справочники диалога — те же, что у реестра: значения уходят в команду
  // создания, и расхождение со справочником сервера её отобьёт.
  const stageRows = useDictRows('stage')
  const contracts = useContracts()
  const areaRows = useDictRows('area')
  const me = useMe().data
  /* Занятые контракты. Реестр на контракт один, и предлагать занятый значит
     обещать номер, которого сервер не выдаст: отказ приходил бы после того,
     как человек заполнил форму. */
  const mdrsQuery = useList<Mdr>('mdrs', { page: 0, pageSize: 200, sort: [{ id: 'id', desc: false }] })
  const taken = new Set((mdrsQuery.data?.rows ?? []).map((r) => r.contract).filter(Boolean))
  const [stage, setStage] = useState('')
  const [contract, setContract] = useState('')
  const [title, setTitle] = useState('')
  /* Пустое состояние Select — это первая строка справочника: её и показывает
     браузер. Отправить при этом пустую строку значило бы разойтись с тем, что
     человек видит на экране. */
  const stageCode = stage || stageRows[0]?.code || ''
  const free = contracts.filter((c) => !taken.has(c.code))
  /* Реестр собственной документации заказчика: ИРД, технические условия,
     задания на проектирование. Подрядчика у него нет, и сервер такой реестр
     принимает (contract = null), а выбрать это было нечем: список состоял из
     одних контрактов, и когда все они заняты, экран запирал сам себя. */
  /* Пустое состояние Select показывает первую доступную строку. Когда свободных
     контрактов нет, ею оказывается «без контракта», и состояние обязано
     совпасть с тем, что видно, — иначе экран говорит одно, а команда шлёт другое. */
  const isOwn = contract === OWN || (contract === '' && free.length === 0)
  const contractCode = isOwn ? '' : (contract || free[0]?.code || '')
  const chosen = contracts.find((c) => c.code === contractCode)
  /* Дисциплины у реестра больше нет: она свойство СТРОКИ. Реестр по контракту
     идёт по всем дисциплинам сразу, а в коде своего реестра на её месте стоит
     OWN — так его собирает сервер. */
  /* Объект — только у своего реестра: он стоит в его коде (MDR-<объект>-<дисц>).
     У реестра по контракту объекта нет: контракт идёт по нескольким объектам, и
     объект задаётся у документа, где он и входит в номер. */
  const [area, setArea] = useState('')
  /* Маска номера документа. По ней сервер нумерует документы реестра, и у
     разных реестров она разная — потому и выбирается здесь, а не берётся одна
     на проект. */
  /* Маска знает свою стадию: у стадии BD номер документа свой (проект, стороны,
     лист). Показываем только те, что реестру годятся, — сервер требует того же,
     и список из негодных обещал бы номер, которого не выдадут. */
  const docMasks = (useCoding().data?.docs ?? []).filter((m) => !m.stage || m.stage === stageCode)
  const [docMask, setDocMask] = useState('')
  const docMaskCode = docMasks.some((m) => m.code === docMask) ? docMask : docMasks[0]?.code || ''
  /* Код собирается маской реестра — той же, что исполняет сервер. Своей
     формулы у диалога нет: показанный номер обязан совпасть с созданным, а
     маску реестра администратор вправе пересобрать. */
  /* Вид номера документа этого реестра. Пример из справочника кодирования
     показывает маску вообще — «DD-2100-PI-DR-221», — а стадию и объект номер
     берёт у РЕЕСТРА: на реестре стадии AB номера будут AB-…, и человек,
     поверивший примеру, проставит в файле номера, которые сервер отобьёт.
     Правило то же, что у сервера (DocumentNumber.shape). */
  const docShape = (m: CodingDoc | undefined): string => {
    const segments = m?.segments ?? []
    if (!segments.length) return ''
    return segments.map((s) => {
      if (s.srcKind === 'seq') return '#'.repeat(s.width && s.width > 0 ? s.width : 0) || '#…'
      if (s.srcKind === 'const') return s.value ?? s.example
      if (s.srcKind === 'stage') return stageCode
      if (s.srcKind === 'area') return area || '0000'
      /* Титул в номере — свойство строки, и на реестре он неизвестен: маска
         показывается тем же угловым видом, каким его пишет сервер. */
      if (s.srcKind === 'titul') return t(lang, '<титул>', '<title>')
      if (s.srcKind === 'disc') return t(lang, '<дисц>', '<disc>')
      if (s.srcKind === 'type') return t(lang, '<тип>', '<type>')
      return s.example
    }).filter((p) => p !== '').join(m?.sep ?? '-')
  }
  const mdrMask = useCoding().data?.others?.find((o) => o.id === 'mdr')
  /* Свой реестр называется объектом и дисциплиной — так его собирает сервер
     (RegisterCommands: "MDR-" + area + '-' + disc|OWN), и показанный номер
     обязан совпасть с созданным. */
  const code = isOwn
    ? 'MDR-' + (area || '0000') + '-OWN'
    : contractCode ? assembleMdrCode(mdrMask, contractCode) : '—'

  const create = useCommand<{ area: string; title: string; owner: string; stage: string; contract: string; docMask: string }, { id: string }>({
    path: () => '/mdrs',
    body: (a) => a,
    invalidate: () => [qk.all('mdrs'), qk.all('audit')],
  })
  // Ответственный — тот, кто завёл реестр. Сервер это поле не выводит, а
  // подставить в него что-то от себя экран не вправе.
  const submit = () => create.mutate(
    { area, title: title.trim(), owner: me?.user.name ?? '', stage: stageCode, contract: contractCode,
      docMask: docMaskCode },
    { onSuccess: (r) => { onClose(); onCreated(r.id) } },
  )

  return (
    <DlgShell open={open} onClose={onClose} sizeKey="mdr-create" width={680} title={t(lang, 'Создать реестр MDR', 'Create MDR register')} subtitle={t(lang, 'Контракт · стадия · титул', 'Contract · stage · title')}
      footer={<><Button variant="secondary" onClick={onClose} disabled={create.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button><Button variant="primary" iconLeft={<Icon name="plus" size={14} />} onClick={submit} disabled={create.isPending || !title.trim() || (!isOwn && !contractCode)} title={!isOwn && !contractCode ? t(lang, 'Свободных контрактов нет — заведите контракт в «Администрировании» или выберите реестр без контракта', 'No free contracts — add one under Administration or pick a register without a contract') : title.trim() ? undefined : t(lang, 'Укажите наименование реестра', 'Enter the register title')}>{create.isPending ? t(lang, 'Создаём…', 'Creating…') : t(lang, 'Создать реестр', 'Create')}</Button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        {/* Контракт — ключ реестра: он один на контракт, и подрядчика реестр
            берёт отсюда же, а не отдельным полем. Сроки показаны рядом: по ним
            сервер проверяет плановые даты представления документов. */}
        <div style={{ gridColumn: '1 / -1' }}><Select label={t(lang, 'Контракт', 'Contract')} value={contract} onChange={(e) => setContract(e.target.value)} hint={isOwn ? t(lang, 'Документация заказчика: подрядчика нет, реестр наполняет и согласовывает сторона заказчика', 'Owner documentation: no contractor, the owner side fills and approves the register') : chosen ? (chosen.vendor ?? t(lang, 'подрядчик не указан', 'no contractor')) + (chosen.dateStart || chosen.dateEnd ? ' · ' + (chosen.dateStart ?? '…') + ' — ' + (chosen.dateEnd ?? '…') : '') : contracts.length ? t(lang, 'Все контракты уже ведутся — свой реестр заказчика заводится строкой «без контракта»', 'Every contract already has a register — use «no contract» for an owner register') : t(lang, 'В проекте нет контрактов', 'No contracts in the project')}>{contracts.map((c) => <option key={c.code} value={c.code} disabled={taken.has(c.code)}>{c.code} — {c.title}{taken.has(c.code) ? t(lang, ' (реестр уже есть)', ' (register exists)') : ''}</option>)}<option value={OWN}>{t(lang, '— без контракта: своя документация заказчика —', '— no contract: owner documentation —')}</option></Select></div>
        <Select label={t(lang, 'Стадия проектирования', 'Project stage')} value={stage} onChange={(e) => setStage(e.target.value)}>{stageRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        {/* Объект — только у своего реестра: он входит в его код. У реестра по
            контракту объекта нет: контракт идёт по нескольким объектам, и
            объект выбирается у документа, где он входит в номер. */}
        {isOwn ? (
          <div style={{ gridColumn: '1 / -1' }}>
            <DictPicker lang={lang} label={t(lang, 'Объект / площадка', 'Area')} value={area}
                        options={areaRows.map((r) => ({ code: r.code, name: r.name }))}
                        onPick={setArea}
                        hint={t(lang, 'Входит в код своего реестра. Не выбран — в коде встанет 0000.', 'Goes into the owner register code. Left empty, the code carries 0000.')} />
          </div>
        ) : null}
        <div style={{ gridColumn: '1 / -1' }}><Input label={t(lang, 'Наименование реестра', 'Register title')} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={chosen?.title ?? ''} /></div>
        <div style={{ gridColumn: '1 / -1' }}><Select label={t(lang, 'Маска номера документа', 'Document number mask')} value={docMaskCode} onChange={(e) => setDocMask(e.target.value)} hint={(() => { const shape = docShape(docMasks.find((m) => m.code === docMaskCode))
                              return shape
                                ? t(lang, 'Номера документов этого реестра: ' + shape, 'Document numbers of this register: ' + shape)
                                : t(lang, 'По ней нумеруются документы реестра', 'Documents of this register are numbered by it') })()}>{docMasks.map((m) => <option key={m.code} value={m.code}>{m.title} — {m.example}</option>)}</Select></div>
      </div>
      <PreviewBox label={t(lang, 'Номер реестра', 'Register number')} value={code} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        <Icon name="info" size={13} />{isOwn
          ? t(lang, 'Код своего реестра сервер собирает из объекта. Подрядчика у него нет: подать такой реестр некому, наполняет и согласовывает его сторона заказчика.',
                    'The server builds an owner register code from the area. It has no contractor: there is nobody to submit it to, the owner side fills and approves it.')
          : t(lang, 'Код реестра сервер собирает из контракта: реестр на контракт один и содержит все его документы. Дисциплина — у каждой строки реестра. Контракты и справочники — в «Администрирование».', 'The server builds the register code from the contract: one register per contract, holding all its documents. Discipline belongs to each register row. Contracts and dictionaries live under Administration.')}
      </div>
    </DlgShell>)


}
function PreviewBox({ label, value }: { label: ReactNode; value: ReactNode }) {
  return (
    <div style={{ padding: '12px 14px', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 5 }}>{label}</div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 16, fontWeight: 600, color: 'var(--accent-ink)' }}>{value}</div>
    </div>)


}

// ---- Document creation dialog (manual single-document card) ------------
function DocCreate({ open, onClose, lang, mdrId, onCreated }: { open: boolean; onClose: () => void; lang: Lang; mdrId: string; onCreated: (no: string) => void }) {
  // Реестр и список контрагентов — с сервера: диалог создаёт документ в
  // конкретном реестре, и брать его из памяти значило бы писать не туда.
  const m = useItem<Mdr>('mdrs', mdrId || null).data
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  const discRows = useDictRows('disc')
  // Тип и назначение — коды справочника, а не подписи: код входит в номер
  // документа, который выделяет сервер.
  const typeRows = useDictRows('docType')
  const purposeRows = useDictRows('purpose')
  const [title, setTitle] = useState('')
  const [disc, setDisc] = useState('')
  const [type, setType] = useState('DR')
  const [purpose, setPurpose] = useState('IFR')
  const [rev, setRev] = useState('00')
  /* Сроки по стадиям круга: выдача на рассмотрение, на согласование, приёмка —
     план и прогноз у каждой. Общего поля плана здесь нет: общий план карточки
     это план ТОЙ стадии, в которой документ сейчас, и сервер берёт его оттуда.
     Отдельное поле спрашивало то же самое вторым разом и позволяло двум
     ответам разойтись. Пустая стадия означает «не планируем». */
  const [planIfr, setPlanIfr] = useState('')
  const [planIfa, setPlanIfa] = useState('')
  const [planAfd, setPlanAfd] = useState('')
  const [forecastIfr, setForecastIfr] = useState('')
  const [forecastIfa, setForecastIfa] = useState('')
  const [forecastAfd, setForecastAfd] = useState('')
  const [vendor, setVendor] = useState('')
  const [no, setNo] = useState('')
  /* Титул и объект — свойства ДОКУМЕНТА, а не реестра: реестр по контракту идёт
     по нескольким объектам, и один объект на все его строки был неправдой.
     Титул сильнее: у титула объект ровно один, и он же его и подставляет. */
  const [titul, setTitul] = useState('')
  const [area, setArea] = useState('')
  /* Английское название и лист — колонки бланка заказчика: название печатается
     во входящем трансмиттале рядом с русским, лист стоит в номере стадии BD. */
  const [titleEn, setTitleEn] = useState('')
  const [sheet, setSheet] = useState('')
  const vendors: string[] = Array.from(new Set((vendorsQuery.data?.rows ?? []).map((v) => v.name).filter(Boolean)))
  /* Реестр на контракт дисциплины не имеет — она у каждого документа своя.
     Тогда «— из реестра —» брать неоткуда, и без выбора сервер отобьёт номер:
     маска требует дисциплины. Требуем её здесь, а не показываем отказ после. */
  const titulRows = useDictRows('titul')
  const areaRows = useDictRows('area')
  /* Маска реестра ставит титул в номер — значит титул обязателен: подставить
     его не из чего (у объекта титулов может быть несколько), и сервер откажет
     тем же правилом. Спрашиваем до нажатия, а не отказом после. */
  const docMasks = useCoding().data?.docs ?? []
  const titulNeeded = (docMasks.find((k) => k.code === m?.docMask)?.segments ?? [])
    .some((seg) => seg.srcKind === 'titul')
  /* Объект титула: выбрал титул — объект подставился и дальше не спрашивается. */
  const areaOfTitul = titulRows.find((r) => r.code === titul)?.area ?? ''
  const areaValue = titul ? areaOfTitul : area
  const discFromRegister = (m?.disc ?? '').trim()
  /* Подрядчик реестра: его подставляет сервер, когда поле оставлено пустым. */
  const vendorFromRegister = (m?.vendor ?? '').trim()
  /* Реестр без контракта — своя документация заказчика: подрядчика у него нет
     и взяться ему неоткуда. */
  const ownRegister = !!m && !(m.contract ?? '').trim()
  const discNeeded = !!m && !discFromRegister && !disc

  const create = useCommand<DocRow, { no: string }>({
    path: () => `/mdrs/${encodeURIComponent(mdrId)}/docs`,
    body: (a) => a,
    invalidate: () => [qk.all('mdrs'), qk.all('docs'), qk.all('audit')],
  })
  const submit = () => create.mutate(
    {
      no: no.trim() || undefined, title: title.trim(), titleEn: titleEn.trim() || undefined,
      sheet: sheet.trim() || undefined, disc: disc || undefined, type,
      rev: rev.trim() || undefined, purpose, vendor: vendor || undefined,
      titul: titul || undefined, area: areaValue || undefined,
      planIfr: planIfr.trim() || undefined, planIfa: planIfa.trim() || undefined,
      planAfd: planAfd.trim() || undefined,
      forecastIfr: forecastIfr.trim() || undefined, forecastIfa: forecastIfa.trim() || undefined,
      forecastAfd: forecastAfd.trim() || undefined,
    },
    { onSuccess: (r) => { onClose(); onCreated(r.no) } },
  )

  return (
    <DlgShell open={open} onClose={onClose} sizeKey="doc-create" width={640} title={t(lang, 'Создать документ', 'New document')} subtitle={m ? m.id + ' · ' + m.title : ''}
      footer={<><Button variant="secondary" onClick={onClose} disabled={create.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button><Button variant="primary" iconLeft={<Icon name="file-plus" size={14} />} onClick={submit} disabled={create.isPending || !title.trim() || discNeeded || !areaValue || (titulNeeded && !titul)} title={!title.trim() ? t(lang, 'Укажите наименование документа', 'Enter the document title') : discNeeded ? t(lang, 'Укажите дисциплину: у реестра её нет', 'Choose a discipline: the register has none') : !areaValue ? t(lang, 'Укажите объект: он входит в номер документа', 'Choose the area: it goes into the document number') : titulNeeded && !titul ? t(lang, 'Укажите титул: он входит в номер документа', 'Choose the title: it goes into the document number') : undefined}>{create.isPending ? t(lang, 'Создаём…', 'Creating…') : t(lang, 'Создать документ', 'Create')}</Button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div style={{ gridColumn: '1 / -1' }}><Input label={t(lang, 'Наименование', 'Title')} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t(lang, 'Напр.: План трасс эстакады, лист 6', 'e.g. Drawing title')} /></div>
        <div style={{ gridColumn: '1 / -1' }}><Input label={t(lang, 'Наименование (EN)', 'Title (EN)')} value={titleEn} onChange={(e) => setTitleEn(e.target.value)} placeholder={t(lang, 'Печатается в бланке трансмиттала', 'Printed on the transmittal form')} /></div>
        <Select label={t(lang, 'Дисциплина', 'Discipline')} value={disc} onChange={(e) => setDisc(e.target.value)} hint={discNeeded ? t(lang, 'У реестра дисциплины нет — выберите', 'The register has none — choose one') : undefined}><option value="">{discFromRegister ? t(lang, '— из реестра (' + discFromRegister + ') —', '— from register (' + discFromRegister + ') —') : t(lang, '— не выбрана —', '— not chosen —')}</option>{discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        <Select label={t(lang, 'Тип документа', 'Type')} value={type} onChange={(e) => setType(e.target.value)}>{typeRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        <Select label={t(lang, 'Назначение', 'Purpose')} value={purpose} onChange={(e) => setPurpose(e.target.value)}>{purposeRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        <Input label={t(lang, 'Ревизия', 'Revision')} value={rev} onChange={(e) => setRev(e.target.value)} placeholder="00"
               hint={t(lang, 'Пусто — 00; следующая появится после приёмки в AFD', 'Blank — 00; the next one comes after AFD acceptance')} />
        <Input label={t(lang, 'Лист', 'Sheet')} value={sheet} onChange={(e) => setSheet(e.target.value)} placeholder="00" hint={t(lang, 'Пусто — 00', 'Blank — 00')} />
        {/* Сроки по стадиям круга: план и прогноз у каждой. Новый документ
            планируется вперёд — прошедшие дни в календаре закрыты; проверка
            мягкая и клиентская, потому что перенос идущего проекта идёт
            импортом, и прошедшие планы там законны. Порядок IFR → IFA → AFD и
            попадание в срок контракта проверяет сервер — тем же правилом,
            каким проверяет присланный файл. */}
        <div style={{ gridColumn: '1 / -1', display: 'grid', gap: 10 }}>
          <label style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
            {t(lang, 'Сроки по стадиям', 'Schedule by stage')}
          </label>
          {([
            ['IFR', t(lang, 'на рассмотрение', 'for review'), planIfr, setPlanIfr, forecastIfr, setForecastIfr],
            ['IFA', t(lang, 'на согласование', 'for approval'), planIfa, setPlanIfa, forecastIfa, setForecastIfa],
            ['AFD', t(lang, 'приёмка', 'accepted'), planAfd, setPlanAfd, forecastAfd, setForecastAfd],
          ] as const).map(([stage, note, planValue, setPlanValue, forecastValue, setForecastValue]) => (
            <div key={stage} style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'end' }}>
              <span style={{ flex: '0 0 auto', width: 128, paddingBottom: 8 }}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600, color: 'var(--text-strong)' }}>{stage}</span>
                <span style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}> · {note}</span>
              </span>
              <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                <DateInput value={planValue} onChange={setPlanValue} minDate={today()} lang={lang}
                           placeholder={t(lang, 'План', 'Plan')} />
              </div>
              {/* Прогноз — своя величина: не назван, остаётся равным плану. */}
              <div style={{ flex: '1 1 130px', minWidth: 0 }}>
                <DateInput value={forecastValue} onChange={setForecastValue} minDate={today()} lang={lang}
                           placeholder={t(lang, 'Прогноз', 'Forecast')} />
              </div>
            </div>
          ))}
        </div>
        {/* Контрагента подставляет сервер из реестра: реестр заведён на контракт
            конкретной организации. Поле говорило «— не задан —», и человек читал
            это как «документ останется без стороны» — хотя сторона будет.
            У реестра БЕЗ контракта подрядчика нет вовсе: это своя документация
            заказчика — ИРД, технические условия, задания, — и она ни от кого не
            приходит. Пустая строка выбора тогда не пропуск, а свойство реестра,
            и сказать об этом надо прямо: иначе подстановки ждут напрасно. */}
        {/* У реестра с подрядчиком поле не правится: реестр заведён на контракт
            конкретной организации, и назначить документу другую значит спрятать
            его от той, которая его ведёт, — на имени стоит изоляция подрядчика.
            Сервер отбивает такую попытку тем же правилом. */}
        {/* Титул и объект: у титула объект один, поэтому титул выбирают, а объект
            он подставляет. Объект без титула — законный случай, поэтому его
            оставили выбираемым отдельно. Справочники большие, и выбор идёт
            поиском, а не прокруткой списка. */}
        <DictPicker lang={lang} label={t(lang, 'Титул проекта', 'Project title')} value={titul}
                    options={titulRows.map((r) => ({ code: r.code, name: r.name, note: r.area }))}
                    onPick={(code) => { setTitul(code); if (code) setArea('') }}
                    emptyLabel={t(lang, '— не выбран —', '— none —')}
                    invalid={titulNeeded && !titul}
                    hint={titulNeeded
                      ? t(lang, 'Маска реестра ставит титул в номер — выберите', 'The register mask puts the title into the number — choose one')
                      : titul
                        ? t(lang, 'Объект берётся из титула', 'The area comes from the title')
                        : t(lang, 'Не выбран — объект указывается отдельно', 'Not chosen — pick the area separately')} />
        <DictPicker lang={lang} label={t(lang, 'Объект / площадка', 'Area')} value={areaValue}
                    options={areaRows.map((r) => ({ code: r.code, name: r.name }))}
                    onPick={setArea} disabled={!!titul} invalid={!areaValue}
                    emptyLabel={t(lang, '— не выбран —', '— none —')}
                    hint={titul
                      ? t(lang, 'Задан титулом', 'Set by the title')
                      : t(lang, 'Входит в номер документа', 'Goes into the document number')} />
        <Select label={t(lang, 'Контрагент', 'Vendor')} value={vendor} onChange={(e) => setVendor(e.target.value)}
                disabled={!!vendorFromRegister}
                hint={vendorFromRegister
                  ? t(lang, 'Подрядчик реестра: у документов реестра он один',
                           'The register contractor: one for every document of the register')
                  : ownRegister
                    ? t(lang, 'Реестр заведён без контракта: документы заказчика приходят не от подрядчика',
                             'The register has no contract: owner documents do not come from a contractor')
                    : t(lang, 'У контракта реестра не указан подрядчик',
                             'The register contract names no contractor')}>
          <option value="">{vendorFromRegister
            ? t(lang, '— из реестра (' + vendorFromRegister + ') —', '— from register (' + vendorFromRegister + ') —')
            : ownRegister
              ? t(lang, '— нет: своя документация заказчика —', '— none: owner documentation —')
              : t(lang, '— не задан —', '— none —')}</option>
          {vendors.map((v) => <option key={v} value={v}>{v}</option>)}
        </Select>
        <div style={{ gridColumn: '1 / -1' }}><Input label={t(lang, '№ документа (пусто = авто)', 'Document № (blank = auto)')} value={no} onChange={(e) => setNo(e.target.value)} placeholder={t(lang, 'авто', 'auto')} /></div>
      </div>
      {/* Порядковый номер выделяет счётчик на сервере, и предсказать его здесь
          нельзя. Показываем либо заданный вручную номер, либо честное «выдаст
          сервер», а не выдуманный образец. */}
      <PreviewBox label={t(lang, 'Будет создан номер', 'Will create №')} value={no.trim() || t(lang, 'выдаст сервер', 'allocated by the server')} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
        <Icon name="info" size={13} />{t(lang, 'Номер собирается на сервере по маске этого реестра. Можно задать вручную.', 'The server builds the number from this register mask; editable.')}
      </div>
    </DlgShell>)
}

// ---- VDR creation dialog (register under a purchase order) -------------
function VdrCreate({ open, onClose, lang, onCreated }: { open: boolean; onClose: () => void; lang: Lang; onCreated: (id: string) => void }) {
  const vendorsQuery = useList<Vendor>('vendors', { page: 0, pageSize: 200, sort: [{ id: 'code', desc: false }] })
  const mdrsQuery = useList<Mdr>('mdrs', { page: 0, pageSize: 100, sort: [{ id: 'id', desc: false }] })
  const discRows = useDictRows('disc')
  const stageRows = useDictRows('stage')
  const contracts = useContracts()
  const [po, setPo] = useState('')
  const [vendor, setVendor] = useState('')
  const [subject, setSubject] = useState('')
  const [disc, setDisc] = useState('MC')
  const [mdrCode, setMdrCode] = useState('')
  const [due, setDue] = useState('')
  const [vdrMask, setVdrMask] = useState('')
  const [stage, setStage] = useState('')
  const [contract, setContract] = useState('')
  // См. MdrCreate: пустое состояние Select — это его первая строка.
  const vendorName = vendor || vendorsQuery.data?.rows[0]?.name || ''
  const stageCode = stage || stageRows[0]?.code || ''
  const contractCode = contract || contracts[0]?.code || ''
  // См. MdrCreate: маска стадии не даётся реестру другой стадии.
  const vdrMasks = (useCoding().data?.docs ?? []).filter((m) => !m.stage || m.stage === stageCode)
  const vdrMaskCode = vdrMasks.some((m) => m.code === vdrMask) ? vdrMask : vdrMasks[0]?.code || ''

  const create = useCommand<{ po: string; vendor: string; subject: string; disc: string; mdrCode?: string; due?: string; stage: string; contract: string; docMask: string }, { id: string }>({
    path: () => '/vdrs',
    body: (a) => a,
    invalidate: () => [qk.all('vdrs'), qk.all('audit')],
  })
  const ready = po.trim() !== '' && subject.trim() !== '' && vendorName !== ''
  const submit = () => create.mutate(
    { po: po.trim(), vendor: vendorName, subject: subject.trim(), disc, mdrCode: mdrCode || undefined, due: due.trim() || undefined, stage: stageCode, contract: contractCode,
      docMask: vdrMaskCode },
    { onSuccess: (r) => { onClose(); onCreated(r.id) } },
  )

  return (
    <DlgShell open={open} onClose={onClose} sizeKey="vdr-create" width={640} title={t(lang, 'Создать реестр VDR', 'Create VDR register')} subtitle={t(lang, 'Заказ · поставщик · предмет поставки', 'Purchase order · vendor · subject')}
      footer={<><Button variant="secondary" onClick={onClose} disabled={create.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button><Button variant="primary" iconLeft={<Icon name="plus" size={14} />} onClick={submit} disabled={create.isPending || !ready} title={ready ? undefined : t(lang, 'Заполните заказ, поставщика и предмет поставки', 'Fill in the order, vendor and subject')}>{create.isPending ? t(lang, 'Создаём…', 'Creating…') : t(lang, 'Создать реестр', 'Create')}</Button></>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <Input label={t(lang, 'Номер заказа (PO)', 'Purchase order')} value={po} onChange={(e) => setPo(e.target.value)} placeholder="PO-4471" />
        <Select label={t(lang, 'Поставщик', 'Vendor')} value={vendor} onChange={(e) => setVendor(e.target.value)}>{(vendorsQuery.data?.rows ?? []).map((v) => <option key={v.code} value={v.name}>{v.name}</option>)}</Select>
        <div style={{ gridColumn: '1 / -1' }}><Input label={t(lang, 'Предмет поставки', 'Subject')} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder={t(lang, 'Напр.: Насосный агрегат ЦН-12', 'e.g. Pump unit')} /></div>
        <Select label={t(lang, 'Дисциплина', 'Discipline')} value={disc} onChange={(e) => setDisc(e.target.value)}>{discRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        <DateInput label={t(lang, 'Срок выпуска', 'Due')} value={due} onChange={setDue} minDate={today()} lang={lang} />
        <div style={{ gridColumn: '1 / -1' }}><Select label={t(lang, 'Маска номера документа', 'Document number mask')} value={vdrMaskCode} onChange={(e) => setVdrMask(e.target.value)} hint={t(lang, 'По ней нумеруются документы реестра', 'Documents of this register are numbered by it')}>{vdrMasks.map((m) => <option key={m.code} value={m.code}>{m.title} — {m.example}</option>)}</Select></div>
        <Select label={t(lang, 'Стадия проектирования', 'Project stage')} value={stage} onChange={(e) => setStage(e.target.value)}>{stageRows.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</Select>
        <Select label={t(lang, 'Контракт', 'Contract')} value={contract} onChange={(e) => setContract(e.target.value)}>{contracts.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.title}{c.vendor ? ' (' + c.vendor + ')' : ''}</option>)}</Select>
        <div style={{ gridColumn: '1 / -1' }}><Select label={t(lang, 'Связанный реестр MDR', 'Linked MDR register')} value={mdrCode} onChange={(e) => setMdrCode(e.target.value)}><option value="">{t(lang, '— не задан —', '— none —')}</option>{(mdrsQuery.data?.rows ?? []).map((r) => <option key={r.id} value={r.id}>{r.id} — {r.title}</option>)}</Select></div>
      </div>
      {/* Код реестра сервер собирает как VDR- + номер заказа. */}
      <PreviewBox label={t(lang, 'Номер реестра', 'Register number')} value={po.trim() ? 'VDR-' + po.trim() : '—'} />
    </DlgShell>)
}

// ---- Register import ---------------------------------------------------
/* Разбор файла (toDocRow) живёт в lib/excel рядом с выгрузкой: только там
   видно, что шапка выгрузки и шапка импорта — одна и та же на всех языках. */
interface ImportResult {
  register: string
  /** Загрузка: сколько строк создано. У сухого прогона поля нет. */
  created?: number
  /** Сколько уже заведённых строк изменено присланным файлом. Сервер это
      считал с самого начала, а окно показывало «Принято 0» — по виду не
      происходило ничего, хотя реестр только что изменён. */
  updated?: number
  /** Сухой прогон: сколько строк проверено и сколько из них прошло. */
  checked?: number
  accepted?: number
  updates?: number
  rejected: number
  errors: { row: number; reason: string }[]
  /** Строки реестра, которых в присланном файле нет: их исключили из объёма. */
  missing?: string[]
}

/* Отчёт сервера нумерует строки ЗАПРОСА, а человек ищет строку в Excel. Между
   ними стоит шапка, заголовок над ней и пустые строки, поэтому номер
   переводится по списку строк файла: иначе «строка 7» указывает на соседнюю. */
function onFileLines(result: ImportResult, lines: number[]): ImportResult {
  return { ...result, errors: result.errors.map((e) => ({ ...e, row: lines[e.row - 1] ?? e.row })) }
}

/* Итог импорта показывается целиком и всегда, а не всплывающим сообщением:
   загрузка реестра — редкая и дорогая операция, и человек обязан увидеть, что
   именно не прошло. Импорт, молча пропустивший половину строк, хуже отказа. */
function ImportReport({ result, lang, onClose }: { result: ImportResult | null; lang: Lang; onClose: () => void }) {
  /* Сухой прогон и загрузка отвечают одним и тем же построчным отчётом, поэтому
     окно одно: два похожих окна однажды разошлись бы в подписях. Отличает их
     то, что вернул сервер, — «создано» есть только у загрузки. */
  const dry = result != null && result.created === undefined
  return (
    <DlgShell open={result !== null} onClose={onClose} sizeKey="register-import" width={560}
      title={dry ? t(lang, 'Проверка файла реестра', 'Register file check') : t(lang, 'Импорт реестра', 'Register import')}
      subtitle={result?.register}
      footer={<Button variant="primary" onClick={onClose}>{t(lang, 'Закрыть', 'Close')}</Button>}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <PreviewBox label={dry ? t(lang, 'Строк прошло', 'Rows passed') : t(lang, 'Принято строк', 'Rows created')}
                    value={(dry ? result?.accepted : result?.created) ?? 0} />
        {/* Изменённые строки: файл двигает сроки у трёхсот заведённых, и «принято 0»
            читалось как «ничего не произошло». */}
        <PreviewBox label={t(lang, 'Изменено строк', 'Rows updated')}
                    value={(dry ? result?.updates : result?.updated) ?? 0} />
        <PreviewBox label={dry ? t(lang, 'С ошибками', 'With errors') : t(lang, 'Отклонено', 'Rejected')} value={result?.rejected ?? 0} />
      </div>
      {/* Строки реестра, которых в файле нет: их исключили из объёма, и до сих
          пор об этом молчали — исключённые оставались в плане и в просрочке. */}
      {result?.missing && result.missing.length ? (
        <div style={{ marginTop: 14, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', background: 'var(--surface-sunken)' }}>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginBottom: 6 }}>
            {t(lang, `В файле нет строк реестра: ${result.missing.length}`, `Register rows missing from the file: ${result.missing.length}`)}
          </div>
          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, color: 'var(--text-muted)', maxHeight: 120, overflow: 'auto' }}>
            {result.missing.slice(0, 40).join(', ')}{result.missing.length > 40 ? ' …' : ''}
          </div>
          <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 6 }}>
            {t(lang, 'Загрузка их не трогает: исключение из объёма — решение, а не следствие файла. Аннулируйте строки, если они исключены.',
                     'The import leaves them alone: dropping scope is a decision, not a side effect of a file. Void them if they are out of scope.')}
          </div>
        </div>
      ) : null}
      {dry && result && result.errors.length === 0 ? (
        <div style={{ marginTop: 14, padding: '10px 12px', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
          {t(lang, 'Файл пройдёт загрузку целиком: ни одна строка не отклонена.',
                   'The file will import in full: no row was rejected.')}
        </div>
      ) : null}
      {result && result.errors.length ? (
        <div style={{ marginTop: 14, maxHeight: 260, overflow: 'auto', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)' }}>
          {result.errors.map((e) => (
            <div key={e.row} style={{ display: 'flex', gap: 10, padding: '8px 12px', borderBottom: '1px solid var(--border-subtle)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)' }}>
              <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--text-subtle)', flex: '0 0 auto' }}>{t(lang, 'строка', 'row')} {e.row}</span>
              <span style={{ color: 'var(--danger-ink)' }}>{e.reason}</span>
            </div>
          ))}
        </div>
      ) : null}
    </DlgShell>)
}

/**
 * Массовая правка выбранных строк реестра.
 *
 * <p>Пустое поле означает «не менять»: правка сроков не должна заодно
 * переписывать назначение выпуска, которого никто не трогал. Сервер правит
 * построчно теми же правилами, что и одиночную карточку, и называет строки,
 * которые не прошли, — массовая правка, отменившая себя из-за одной строки,
 * заставляет искать виноватую руками.
 */
/* Аннулирование выбранных строк. Причина обязательна: «строки не стало» без
   объяснения через месяц читается как потеря данных, а не как решение. */
function BulkVoidDialog({ open, onClose, lang, nos, onDone }: {
  open: boolean
  onClose: () => void
  lang: Lang
  nos: string[]
  onDone: () => void
}) {
  const [reason, setReason] = useState('')
  const drop = useCommand<{ reason: string }, { voided: number; rejected: number }>({
    path: () => '/docs:void',
    body: (a) => ({ nos, reason: a.reason }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  return (
    <DlgShell open={open} onClose={onClose} sizeKey="bulk-void" width={520}
      title={t(lang, 'Аннулировать выбранные строки', 'Void selected rows')}
      subtitle={t(lang, `${nos.length} документов — строки останутся в истории`,
                        `${nos.length} documents — the rows stay in history`)}
      footer={<><Button variant="secondary" onClick={onClose} disabled={drop.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="danger" disabled={drop.isPending || reason.trim() === ''}
                onClick={() => drop.mutate({ reason: reason.trim() }, {
                  onSuccess: (r) => {
                    toast({ tone: r.rejected ? 'danger' : 'success',
                      title: t(lang, 'Строки аннулированы', 'Rows voided'),
                      text: t(lang, `аннулировано ${r.voided}, отклонено ${r.rejected}`,
                                    `voided ${r.voided}, rejected ${r.rejected}`) })
                    onDone()
                    onClose()
                  },
                  onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не вышло', 'Failed'), text: e.message }),
                })}>{drop.isPending ? t(lang, 'Аннулируем…', 'Voiding…') : t(lang, 'Аннулировать', 'Void')}</Button></>}>
      <Field label={t(lang, 'Причина', 'Reason')}>
        <Input value={reason} onChange={(e) => setReason(e.target.value)}
               placeholder={t(lang, 'Например: объём сокращён по ДС №3', 'e.g. scope reduced by amendment 3')} />
      </Field>
    </DlgShell>)
}

function BulkEditDialog({ open, onClose, lang, nos, onDone }: {
  open: boolean
  onClose: () => void
  lang: Lang
  nos: string[]
  onDone: () => void
}) {
  const [form, setForm] = useState({ purpose: '', disc: '', reviewer: '', plan: '', forecast: '' })
  /* Назначение — из справочника, а не свободной строкой: это был последний
     ввод purpose текстом, и опечатка разъезжалась по сотне строк разом.
     Переходы всё равно судит сервер — схема назначений (V97). */
  const purposeRows = useDictRows('purpose')
  /* Сдвиг сроков: отдельная команда, потому что это другое действие — не
     «назначить дату», а «подвинуть график». */
  const [shiftDays, setShiftDays] = useState('')
  const [shiftStage, setShiftStage] = useState('')
  const shift = useCommand<{ days: number; stage?: string }, { changed: number; rejected: number }>({
    method: 'PATCH',
    path: () => '/docs:shift',
    body: (a) => ({ nos, days: a.days, stage: a.stage }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  const edit = useCommand<Record<string, string>, { changed: number; rejected: number }>({
    method: 'PATCH',
    path: () => '/docs:bulk',
    body: (a) => ({ nos, patch: a }),
    invalidate: () => [qk.all('docs'), qk.all('vdocs'), qk.all('mdrs'), qk.all('vdrs'), qk.all('audit')],
  })
  const patch = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim() !== ''))
  return (
    <DlgShell open={open} onClose={onClose} sizeKey="bulk-edit" width={560}
      title={t(lang, 'Изменить выбранные строки', 'Edit selected rows')}
      subtitle={t(lang, `${nos.length} документов`, `${nos.length} documents`)}
      footer={<><Button variant="secondary" onClick={onClose} disabled={edit.isPending}>{t(lang, 'Отмена', 'Cancel')}</Button>
        <Button variant="primary" disabled={edit.isPending || Object.keys(patch).length === 0}
                onClick={() => edit.mutate(patch, {
                  onSuccess: (r) => {
                    toast({ tone: r.rejected ? 'danger' : 'success',
                      title: t(lang, 'Строки изменены', 'Rows updated'),
                      text: t(lang, `изменено ${r.changed}, отклонено ${r.rejected}`,
                                    `updated ${r.changed}, rejected ${r.rejected}`) })
                    onDone()
                    onClose()
                  },
                })}>{edit.isPending ? t(lang, 'Меняем…', 'Updating…') : t(lang, 'Применить', 'Apply')}</Button></>}>
      <div style={{ display: 'grid', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Select label={t(lang, 'Назначение', 'Purpose')} value={form.purpose}
                  onChange={(e) => setForm((f) => ({ ...f, purpose: e.target.value }))}>
            <option value="">{t(lang, '— не менять —', '— keep —')}</option>
            {purposeRows.map((r) => <option key={r.code} value={r.code}>{r.code}</option>)}
          </Select>
          <Input label={t(lang, 'Дисциплина', 'Discipline')} value={form.disc} placeholder={t(lang, 'не менять', 'keep')}
                 onChange={(e) => setForm((f) => ({ ...f, disc: e.target.value }))} />
        </div>
        <Input label={t(lang, 'Рецензент', 'Reviewer')} value={form.reviewer} placeholder={t(lang, 'не менять', 'keep')}
               onChange={(e) => setForm((f) => ({ ...f, reviewer: e.target.value }))} />
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Input label={t(lang, 'План', 'Plan')} value={form.plan} placeholder={t(lang, 'ДД.ММ.ГГГГ', 'DD.MM.YYYY')}
                 onChange={(e) => setForm((f) => ({ ...f, plan: e.target.value }))} />
          <Input label={t(lang, 'Прогноз', 'Forecast')} value={form.forecast} placeholder={t(lang, 'ДД.ММ.ГГГГ', 'DD.MM.YYYY')}
                 onChange={(e) => setForm((f) => ({ ...f, forecast: e.target.value }))} />
        </div>
        <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>
          {t(lang, 'Пустое поле не меняется. План правится только в наполняемом реестре: в согласованном срок двигают изменением реестра.',
            'A blank field is left alone. The plan changes only while the register is open: in an approved one, dates move by amending it.')}
        </div>
        {/* Сдвиг на N дней: одна дата всем уничтожает график, а сдвигают его
            постоянно — подрядчик получил доступ на площадку на месяц позже,
            заказчик перенёс веху. Двигается прогноз: план — обязательство. */}
        <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: 12, display: 'grid', gap: 8 }}>
          <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)' }}>
            {t(lang, 'Сдвинуть сроки, не назначая общей даты', 'Shift dates without setting a common one')}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 180px auto', gap: 10, alignItems: 'end' }}>
            <Input label={t(lang, 'Дней', 'Days')} value={shiftDays} onChange={(e) => setShiftDays(e.target.value)}
                   placeholder="+14" />
            <Select label={t(lang, 'Что двигаем', 'What moves')} value={shiftStage} onChange={(e) => setShiftStage(e.target.value)}>
              <option value="">{t(lang, 'Общий срок документа', 'Document date')}</option>
              <option value="IFR">IFR</option>
              <option value="IFA">IFA</option>
              <option value="AFD">AFD</option>
            </Select>
            <Button variant="secondary" disabled={shift.isPending || !Number.parseInt(shiftDays, 10)}
                    title={t(lang, 'Каждая строка двигается от своего прогноза, а нет прогноза — от плана',
                                  'Each row moves from its own forecast, or from the plan when there is none')}
                    onClick={() => shift.mutate({ days: Number.parseInt(shiftDays, 10), stage: shiftStage || undefined }, {
                      onSuccess: (r) => {
                        toast({ tone: r.rejected ? 'danger' : 'success',
                          title: t(lang, 'Сроки сдвинуты', 'Dates shifted'),
                          text: t(lang, `сдвинуто ${r.changed}, отклонено ${r.rejected}`,
                                        `shifted ${r.changed}, rejected ${r.rejected}`) })
                        onDone()
                        onClose()
                      },
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не сдвинуто', 'Not shifted'), text: e.message }),
                    })}>{shift.isPending ? t(lang, 'Двигаем…', 'Shifting…') : t(lang, 'Сдвинуть', 'Shift')}</Button>
          </div>
        </div>
      </div>
    </DlgShell>
  )
}

/**
 * Массовая загрузка файлов реестра.
 *
 * <p>Реальная подача — это сотни PDF одним каталогом, и открывать под каждый
 * карточку значит не уметь принимать документацию. Файлы разносятся по строкам
 * реестра по номеру в имени («№_роль.расширение») — тем же разбором, каким
 * это уже делает пакет трансмиттала; непривязанные названы отдельно, а не
 * потеряны.
 */
function BulkFilesDialog({ open, onClose, lang, title, load }: {
  open: boolean
  onClose: () => void
  lang: Lang
  title: string
  load: () => Promise<{ no: string; rev: string }[]>
}) {
  const [docs, setDocs] = useState<{ no: string; rev: string }[] | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  useEffect(() => {
    if (!open) return
    let alive = true
    load()
      .then((rows) => { if (alive) setDocs(rows) })
      .catch((e: unknown) => { if (alive) setFailed(e instanceof Error ? e.message : String(e)) })
    return () => { alive = false }
    // Состав реестра читается один раз на открытие: пока диалог открыт, он не
    // меняется, а перечитывание на каждый рендер стоило бы сотен строк.
  }, [open, load])
  return (
    <DlgShell open={open} onClose={onClose} sizeKey="bulk-files" width={720}
      title={t(lang, 'Загрузка файлов реестра', 'Bulk file upload')} subtitle={title}
      footer={<Button variant="secondary" onClick={onClose}>{t(lang, 'Закрыть', 'Close')}</Button>}>
      {failed ? (
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--danger-ink)' }}>{failed}</div>
      ) : docs === null ? (
        <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>{t(lang, 'Читаем состав реестра…', 'Loading the register content…')}</div>
      ) : (
        <TrmFilesZone docs={docs} lang={lang} />
      )}
    </DlgShell>
  )
}

// ---- MDR register ------------------------------------------------------
/** Размер страницы реестра. Сервер отдаёт ровно эту порцию, а не весь набор. */
/* Порция рельсы реестров. Двести — потолок страницы на сервере
   (PageResponse.MAX_PAGE_SIZE), и просить больше бессмысленно: рельса
   набирается порциями, а не «всё сразу». */
const RAIL_PAGE = 100
const PAGE_SIZE = 50
/* Размер порции выгрузки. Совпадает с потолком страницы на сервере
   (PageResponse.MAX_PAGE_SIZE): просить больше бессмысленно — обрежет. */
const EXPORT_CHUNK = 200
/* Потолок массовой команды. Совпадает с IMPORT_LIMIT на сервере: он отобьёт
   больше двух тысяч строк за раз, и выбирать сверх этого — обещать невозможное. */
const BULK_LIMIT = 2000

export function Pager({ page, pageCount, total, lang, onPage }: { page: number; pageCount: number; total: number; lang: Lang; onPage: (p: number) => void }) {
  if (total === 0) return null
  return (
    <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 12, padding: '8px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--surface-card)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
      <span style={{ fontFamily: 'var(--font-mono)' }}>{t(lang, 'Всего', 'Total')}: {total}</span>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button variant="secondary" size="sm" disabled={page <= 0} onClick={() => onPage(page - 1)}>{t(lang, 'Назад', 'Prev')}</Button>
        <span style={{ fontFamily: 'var(--font-mono)' }}>{page + 1} / {pageCount}</span>
        <Button variant="secondary" size="sm" disabled={page >= pageCount - 1} onClick={() => onPage(page + 1)}>{t(lang, 'Вперёд', 'Next')}</Button>
      </div>
    </div>
  )
}

export function MdrRegister({ lang = 'ru', onOpenDoc, onOpenViewer, onOpenMdr }: { lang?: Lang; onOpenDoc: (no: string) => void; onOpenViewer?: (no: string) => void; onOpenMdr?: (id: string) => void }) {
  /* Проектные атрибуты: по их заголовкам импорт узнаёт колонки файла заказчика. */
  const attrDefs = useDocAttrs()
  const [chosenMdr, setChosenMdr] = useState('')
  const [q, setQ] = useState(''); const [disc, setDisc] = useState(''); const [life, setLife] = useState('')
  const [slice, setSlice] = useState<Slice>('')
  /* Отбор по проектной колонке: пара «колонка + значение» уходит на сервер
     полем «attr.<код>» — его понимают и сервер, и мок. */
  const [attrPick, setAttrPick] = useState({ code: '', value: '' })
  const attrQuery = attrPick.code && attrPick.value ? { ['attr.' + attrPick.code]: [attrPick.value] } : {}
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<React.Key>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  const [docOpen, setDocOpen] = useState(false)
  /* Mount переживает закрытие на длину анимации ухода — см. useExitMount. */
  const createMount = useExitMount(createOpen)
  const docMount = useExitMount(docOpen)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const fileRef = useRef<HTMLInputElement | null>(null)
  // Подрядчик приходит на тот же экран: сервер отдаёт ему только его реестр.
  const external = !!useMe().data?.vendor
  /* Подача реестра на согласование. Предмет подачи — сам реестр: заказчик
     согласовывает состав и сроки целиком, а не документ за документом. */
  const submitRegister = useCommand<{ code: string }, { id: string }>({
    path: (a) => `/mdrs/${encodeURIComponent(a.code)}/submit`,
    invalidate: () => [qk.all('mdrs'), qk.all('transmittals'), ...inboxKeys, qk.all('audit')],
  })
  /* Изменение согласованного реестра. Инициировать может любая сторона: сроки
     сдвигает подрядчик, объём — заказчик. До нового согласования официальным
     остаётся предыдущее. */
  const amendRegister = useCommand<{ code: string }, { status: string }>({
    path: (a) => `/mdrs/${encodeURIComponent(a.code)}/amend`,
    invalidate: () => [qk.all('mdrs'), qk.all('audit')],
  })

  /* Отмена начатого изменения и закрытие реестра. Изменение отменяется, только
     пока состав не тронут; закрытый реестр открывается обратно — закрывают и
     по ошибке. */
  const cancelAmend = useCommand<{ code: string }, { status: string }>({
    path: (a) => `/mdrs/${encodeURIComponent(a.code)}/cancel-amend`,
    invalidate: () => [qk.all('mdrs'), qk.all('audit')],
  })
  const closeRegister = useCommand<{ code: string; open?: boolean }, { status: string }>({
    path: (a) => `/mdrs/${encodeURIComponent(a.code)}/${a.open ? 'reopen' : 'close'}`,
    invalidate: () => [qk.all('mdrs'), qk.all('audit')],
  })

  // Реестры и документы приходят с сервера. Фильтрация, сортировка и разбиение
  // на страницы выполняются там же: реестр может насчитывать сотни тысяч строк,
  // и грузить его целиком нельзя (см. src/data/contracts.ts).
  /* Рельса реестров обрывалась на сотом молча: на проекте их десятки, а с
     разбивкой по титулам — сотни, и человек решал, что реестра нет. */
  const [mdrSearch, setMdrSearch] = useState('')
  const [mdrLimit, setMdrLimit] = useState(RAIL_PAGE)
  const mdrsQuery = useList<Mdr>('mdrs', {
    page: 0, pageSize: mdrLimit, sort: [{ id: 'id', desc: false }],
    q: mdrSearch.trim() || undefined,
  })
  const mdrs = mdrsQuery.data?.rows ?? []
  const activeMdr = chosenMdr || mdrs[0]?.id || ''
  const mdr = mdrs.find((m) => m.id === activeMdr)

  /* Порядок реестра. Держит экран, считает сервер: щелчок по заголовку
     сортировал загруженную страницу — пятьдесят строк из полутора тысяч, — и
     даты при этом сравнивались как текст «ДД.ММ.ГГГГ». Снятый порядок
     возвращает нумерацию: набор обязан оставаться определённым. */
  const [order, setOrder] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const sortSpec = [{ id: order?.key ?? 'no', desc: order?.dir === 'desc' }]
  /* Отбор в шапке таблицы. Держит экран, применяет сервер: галочки отбирали
     загруженную страницу — пятьдесят строк из полутора тысяч. */
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({})
  const docFilters = intersectFilters({
    mdr: [activeMdr],
    ...(disc ? { disc: [disc] } : {}),
    ...attrQuery,
    ...sliceFilters(slice, life),
  }, colFilters)

  const docsQuery = useList<Doc>(
    'docs',
    { page, pageSize: PAGE_SIZE, sort: sortSpec, filters: docFilters, q: q || undefined },
    { enabled: activeMdr !== '' },
  )
  const docs = docsQuery.data?.rows ?? []
  /* Пустая таблица объясняется тем, что есть на самом деле. «Нет документов по
     заданным фильтрам» при пустом поиске и закрытой панели отбора отправляло
     человека снимать несуществующие условия вместо того, чтобы наполнить
     реестр. */
  const narrowed = Boolean(q || disc || life || slice || attrPick.code || Object.keys(colFilters).length)
  const docsEmpty = narrowed ? undefined : (
    <EmptyState icon="file-plus" title={t(lang, 'В реестре пока нет документов', 'No documents in this register yet')}
                text={t(lang, 'Состав загружают файлом реестра («Проверить файл») или заводят строкой — «Создать документ».',
                              'Load the content from a register file (“Check file”), or add rows one by one with “New document”.')} />
  )

  /* Значения проектных колонок — одним запросом на реестр: спрашивать их
     документ за документом значит полторы тысячи запросов ради одной таблицы.
     Без заведённых атрибутов запрос не идёт вовсе. */
  const attrValues = useSub<{ rows: Record<string, Record<string, string>> }>(
    attrDefs.length && activeMdr ? `/mdrs/${encodeURIComponent(activeMdr)}/attributes` : null)
  const docAttrs = { defs: attrDefs, values: attrValues.data?.rows ?? {} }

  const pickMdr = (id: string) => { setChosenMdr(id); setSelected(new Set()); setPage(0) }
  // Смена фильтра сбрасывает страницу: иначе на второй странице нового набора
  // легко оказаться за его пределами.
  const setFilter = (apply: () => void) => { apply(); setPage(0) }

  /* Выгрузка берёт весь отфильтрованный набор, а не показанную страницу:
     «экспорт реестра» должен означать реестр.

     Страница ограничена сверху (MAX_PAGE_SIZE на сервере), поэтому набор
     забирается порциями. Запрос «дай сразу все» молча вернул бы первые 200
     строк, и выгрузка выглядела бы полной, не будучи ею. */
  const collectAll = async (): Promise<Doc[]> => {
    const filters = docFilters
    const rows: Doc[] = []
    let page = 0
    let pageCount = 1
    do {
      const chunk = await fetchList<Doc>('docs', {
        page,
        pageSize: EXPORT_CHUNK,
        sort: [{ id: 'no', desc: false }],
        filters,
        q: q || undefined,
      })
      rows.push(...chunk.rows)
      pageCount = chunk.pageCount
      page += 1
    } while (page < pageCount)
    return rows
  }


  /* Выбрать весь отфильтрованный набор, а не показанную страницу. Набор
     забирается тем же обходом, что и выгрузка: сервер отдаёт страницами.

     Массовые команды принимают не больше BULK_LIMIT строк за раз — больше
     выбрать нельзя, и молчать об этом нельзя тем более: выбор выглядел бы
     полным, не будучи им. */
  const selectAllFiltered = () => {
    collectAll().then((rows) => {
      const take = rows.slice(0, BULK_LIMIT)
      setSelected(new Set(take.map((d) => d.no)))
      if (rows.length > BULK_LIMIT) {
        toast({ tone: 'info', title: t(lang, 'Выбрано не всё', 'Not everything selected'),
          text: t(lang, `${BULK_LIMIT} из ${rows.length}: за раз обрабатывается не больше ${BULK_LIMIT} строк — сузьте фильтр`,
                        `${BULK_LIMIT} of ${rows.length}: at most ${BULK_LIMIT} rows are processed at once — narrow the filter`) })
      }
    }).catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Не выбрано', 'Selection failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }
  /* Книгу собирает СЕРВЕР: выпадающие списки справочников браузер не пишет
     (SheetJS их не умеет), а правила реестра — маску номера и стадию — знает
     только он. Отбор экрана в книгу не переносится: файл описывает реестр
     целиком, и «выгрузил половину» — худшее, что может случиться с реестром,
     который потом грузят обратно. */
  const exportAll = async () => {
    await saveFile(`/mdrs/${encodeURIComponent(activeMdr)}/book?rows=all`)
    noteExport('реестр MDR ' + activeMdr, undefined, mdr?.docs)
  }
  const downloadTemplate = async () => {
    await saveFile(`/mdrs/${encodeURIComponent(activeMdr)}/book?rows=none`)
    noteExport('шаблон реестра MDR ' + activeMdr)
  }

  const runTemplate = () => {
    downloadTemplate().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Шаблон не собран', 'Template failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }
  const runExport = () => {
    exportAll().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }

  const project = useMe().data?.project ?? ''

  /* Массовая загрузка реестра. Файл разбирается здесь, проверка строк — на
     сервере: только он знает справочники и занятые номера. Отчёт по строкам
     приходит в ответе и показывается целиком. */
  const checkRef = useRef<HTMLInputElement>(null)
  /* Перенос идущего проекта: файл со сданными документами и прошедшими
     сроками. Признак называется явно — он снимает запрет на прошлое и
     принимает состояние с кодом, а рядовой импорт этим притворяться не должен.
     Право на него у того, кто ведёт администрирование: сервер проверит. */
  const [historical, setHistorical] = useState(false)
  /* Право на перенос — то же, что проверяет сервер: запись в разделе
     администрирования. Без неё галку не показываем вовсе. */
  const canMigrate = useMe().data?.perms?.admin === 'rw'
  const importDocs = useCommand<{ rows: DocRow[] }, ImportResult>({
    path: () => `/mdrs/${encodeURIComponent(activeMdr)}/docs:import`,
    body: (a) => ({ rows: a.rows, historical }),
    invalidate: () => [qk.all('mdrs'), qk.all('docs'), qk.all('audit')],
  })
  /* Сухой прогон файла: те же правила, что у загрузки, но ничего не создаётся.
     Регламент разводит их по шагам — подрядчик проверяет заполненный шаблон до
     отправки, координатор проверяет присланный до загрузки, — поэтому это
     отдельная кнопка, а не «загрузите и посмотрите отчёт». */
  const checkDocs = useCommand<{ rows: DocRow[] }, ImportResult>({
    path: () => `/mdrs/${encodeURIComponent(activeMdr)}/docs:validate`,
    body: (a) => ({ rows: a.rows, historical }),
    invalidate: () => [],
  })
  /* Шаг 6 регламента: заказчик передаёт подрядчику ШАБЛОН реестра. Пакет уходит
     без состава документов — карточек ещё нет, их заполняет подрядчик, — и
     предметом пакета становится сам реестр. Файл собирается здесь из текущего
     состава: пустой реестр даёт голую шапку, и это и есть шаблон. */
  const [templateBusy, setTemplateBusy] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const sendTemplate = useCommand<{ recipient: string; registerCode: string }, { id: string }>({
    path: () => '/transmittals',
    body: (a) => ({ purpose: 'IFI', recipient: a.recipient, docNos: [], registerCode: a.registerCode }),
    invalidate: () => [qk.all('transmittals'), qk.all('audit'), ...inboxKeys],
  })
  const handTemplate = () => {
    const recipient = mdr?.vendor
    if (!activeMdr || !recipient) return
    setTemplateBusy(true)
    sendTemplate.mutate({ recipient, registerCode: activeMdr }, {
      onSuccess: (r) => {
        /* Пакет уже создан, а файл ещё нет: об этом и говорим, если загрузка
           сорвётся — иначе координатор решит, что не ушло ничего. */
        /* Шаблон берётся у сервера — той же книгой, что и по кнопке «Шаблон»:
           собранная браузером была без списков справочников и без правил
           реестра, и подрядчик получал файл, ничего не говорящий о номерах. */
        requestFile(`/mdrs/${encodeURIComponent(activeMdr)}/book?rows=none`)
          .then(({ blob, fileName }) => uploadTrmFile(r.id, new File([blob], fileName,
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'other'))
          .then(() => toast({ tone: 'success', title: t(lang, 'Шаблон передан', 'Template handed over'),
            text: r.id + ' · ' + recipient }))
          /* Повторное нажатие завело бы второй пакет, а файл нужен этому:
             говорим, куда идти, — в карточке пакета есть «Приложить файл». */
          .catch((e: unknown) => toast({ tone: 'danger',
            title: t(lang, 'Пакет создан, файл не ушёл', 'Package created, file not uploaded'),
            text: r.id + ' · ' + (e instanceof Error ? e.message : String(e)) + ' · '
              + t(lang, 'приложите шаблон в карточке пакета, не создавая новый',
                        'attach the template in the package card instead of creating a new one') }))
          .finally(() => setTemplateBusy(false))
      },
      onError: (e: unknown) => {
        setTemplateBusy(false)
        toast({ tone: 'danger', title: t(lang, 'Шаблон не передан', 'Handover failed'),
          text: e instanceof Error ? e.message : String(e) })
      },
    })
  }

  /* Выпуск исходящего трансмиттала с реестра заказчика. Адресат — подрядчик
     РЕЕСТРА, а не выделения: реестр MDR заводится на контракт, а у контракта
     сторона одна. Прежде кнопка «Сформировать TRM» здесь была мертва —
     обработчик не передавался вовсе, — а подсказка врала про заказ на
     поставку, которого в MDR нет. */
  const createTrm = useCommand<{ recipient: string; docNos: string[] }, { id: string; status: string; docs: number }>({
    path: () => '/transmittals',
    body: (a) => ({ purpose: 'IFR', recipient: a.recipient, docNos: a.docNos }),
    invalidate: () => [qk.all('transmittals'), qk.all('docs'), qk.all('mdrs'), qk.all('audit'), ...inboxKeys],
  })
  const issueTrm = (recipient: string) => createTrm.mutate(
    { recipient, docNos: Array.from(selected, String) },
    {
      // Выбор снимается только после ответа: до него документы ещё не в TRM.
      onSuccess: (r) => {
        setSelected(new Set())
        toast({ tone: 'success', title: t(lang, 'Трансмиттал выпущен', 'Transmittal issued'),
          text: r.id + ' · ' + r.docs + ' ' + t(lang, 'док.', 'docs') })
      },
    },
  )
  /* Своя документация заказчика (ИРД, ТУ, задания) контракта не имеет, а
     значит и подрядчика: адресовать такой пакет некому. */
  const mdrVendor = mdr?.vendor

  /* Сбой разбора файла в воронку команд не попадает: до сервера дело не дошло.
     Молча ничего не сделать в ответ на выбранный файл нельзя. */
  const runFile = (file: File, dry: boolean) => {
    parseDocs(file)
      .then((sheet) => {
        const rows = sheet.rows.map((r) => toDocRow(r, attrDefs))
        /* Колонки, которые никуда не легли, называются вслух: заказчик
           присылает сорок колонок, система знает двадцать — и остальные
           исчезали молча, а ТДО вёл второй Excel рядом с реестром.

           Смотрим сводную строку листа, а не первую: колонка, пустая в первой
           строке данных («Примечание», «Тег оборудования»), — тоже колонка, и
           раньше она пропадала вместе со своими значениями. */
        const lost = unknownColumns(sampleRow(sheet.rows), attrDefs)
        if (lost.length) {
          toast({ tone: 'info', title: t(lang, 'Колонки файла не разобраны', 'Unmapped columns'),
            text: t(lang, `${lost.slice(0, 6).join(', ')} — заведите атрибуты проекта в администрировании`,
                          `${lost.slice(0, 6).join(', ')} — add project attributes in administration`) })
        }
        const cmd = dry ? checkDocs : importDocs
        cmd.mutate({ rows }, {
          onSuccess: (r) => setImportResult(onFileLines(r, sheet.lines)),
          /* Отказ ВСЕГО запроса (403, замороженный реестр) иначе уходил в общую
             воронку мутаций безымянным тостом: человек только что выбрал файл и
             имеет право узнать, что не приняли именно его и в какой реестр. */
          onError: (e: unknown) => toast({
            tone: 'danger',
            title: dry ? t(lang, 'Файл не проверен', 'File not checked') : t(lang, 'Файл не загружен', 'File not imported'),
            text: `${activeMdr} · ${e instanceof Error ? e.message : String(e)}`,
          }),
        })
      })
      .catch((e: unknown) => {
        toast({ tone: 'danger', title: t(lang, 'Файл не прочитан', 'Cannot read the file'),
          text: e instanceof Error ? e.message : String(e) })
      })
  }

  return (
    /* Высота окна, а не «не меньше окна»: экран сам держит шапку, рельсу
       дисциплин и подвал на месте, а прокручивается только таблица. С minHeight
       наружу вырастала вся страница — уезжали и заголовки столбцов, и рельса,
       на месте которой оставалась пустая белая колонка. Так устроены все
       экраны-мастер-деталь; исключение — витрины (обзор проекта, вход). */
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{project} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Реестр MDR', 'MDR register')}</span>}
        title={t(lang, 'Реестр MDR', 'MDR register')} count={mdrsQuery.isSuccess ? mdrsQuery.data.total + ' ' + t(lang, 'реестров', 'registers') : '—'}
        actions={<>
          <input ref={fileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) runFile(f, false); e.target.value = '' }} />
          <input ref={checkRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) runFile(f, true); e.target.value = '' }} />
          <Button variant="secondary" size="sm" iconLeft={<Icon name="scan-search" size={14} />}
                  disabled={!activeMdr || checkDocs.isPending || !isFillable(mdr?.status)}
                  title={t(lang, 'Прогнать файл по правилам загрузки, ничего не создавая',
                                'Run the file through the import rules without creating anything')}
                  onClick={() => checkRef.current?.click()}>{checkDocs.isPending ? t(lang, 'Проверка…', 'Checking…') : t(lang, 'Проверить файл', 'Check file')}</Button>
          {/* Файлы кладутся пакетом: подача — это сотни PDF, и открывать под
              каждый карточку значит не уметь принимать документацию. */}
          {/* Перенос истории требует прав администрирования, и сервер их
              проверяет (RegisterCommandController.historical). Показывать
              чекбокс тому, у кого их нет, — ловушка: подрядчик ставил галку по
              совету первого же отказа и валил ВЕСЬ запрос 403 про раздел,
              которого не видит. */}
          {canMigrate ? (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}
                   title={t(lang, 'Файл действующего проекта: прошедшие сроки, состояние и код принимаются как есть',
                                 'A file of a running project: past dates, state and code are taken as they are')}>
              <input type="checkbox" checked={historical} onChange={(e) => setHistorical(e.target.checked)} />
              {t(lang, 'Исторический реестр', 'Historical register')}
            </label>
          ) : null}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="paperclip" size={14} />}
                  disabled={!activeMdr}
                  title={t(lang, 'Разнести файлы по строкам реестра по номеру в имени',
                                'Spread files across register rows by the number in the file name')}
                  onClick={() => setBulkOpen(true)}>{t(lang, 'Файлы пакетом', 'Bulk files')}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={14} />}
                  disabled={!activeMdr || importDocs.isPending || !isFillable(mdr?.status)}
                  title={!activeMdr ? t(lang, 'Сначала выберите реестр', 'Select a register first')
                          : !isFillable(mdr?.status) ? t(lang, 'Состав реестра закрыт: он согласован или на рассмотрении',
                                                            'The register content is closed: approved or under review')
                          : undefined}
                  onClick={() => fileRef.current?.click()}>{importDocs.isPending ? t(lang, 'Импорт…', 'Importing…') : t(lang, 'Импорт реестра', 'Import')}</Button>
          {mdr?.status === 'approved' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="pencil" size={14} />}
                    disabled={amendRegister.isPending}
                    title={t(lang, 'Состав снова откроется; согласованная редакция остаётся действующей до нового согласования',
                                  'Content reopens; the approved revision stays in force until the next approval')}
                    onClick={() => amendRegister.mutate({ code: activeMdr }, {
                      onSuccess: () => toast({ tone: 'info',
                        title: t(lang, 'Начато изменение реестра', 'Register amendment started'),
                        text: t(lang, 'Действует прежняя согласованная редакция', 'The previous approved revision stays in force') }),
                    })}>
              {t(lang, 'Изменить реестр', 'Amend')}
            </Button>
          ) : null}
          {mdr?.status === 'amending' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="x" size={14} />}
                    disabled={cancelAmend.isPending}
                    title={t(lang, 'Отменяется, пока состав не тронут: иначе изменение доводят до согласования',
                                  'Cancels while nothing is changed; otherwise carry the amendment through')}
                    onClick={() => cancelAmend.mutate({ code: activeMdr }, {
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Изменение не отменено', 'Not cancelled'), text: e.message }),
                    })}>
              {t(lang, 'Отменить изменение', 'Cancel amendment')}
            </Button>
          ) : null}
          {/* Закрытие — конец работы по реестру, а не удаление: строки читаются,
              история цела, состав больше не меняется. */}
          {mdr?.status === 'approved' || mdr?.status === 'closed' ? (
            <Button variant="secondary" size="sm" iconLeft={<Icon name={mdr?.status === 'closed' ? 'pencil' : 'lock'} size={14} />}
                    disabled={closeRegister.isPending}
                    title={mdr?.status === 'closed'
                      ? t(lang, 'Работа по реестру продолжается', 'Work on the register continues')
                      : t(lang, 'Работа по реестру окончена: состав больше не меняется', 'Work is over: the content is frozen')}
                    onClick={() => closeRegister.mutate({ code: activeMdr, open: mdr?.status === 'closed' }, {
                      onError: (e) => toast({ tone: 'danger', title: t(lang, 'Не вышло', 'Failed'), text: e.message }),
                    })}>
              {mdr?.status === 'closed' ? t(lang, 'Открыть реестр', 'Reopen') : t(lang, 'Закрыть реестр', 'Close')}
            </Button>
          ) : null}
          <Button variant="secondary" size="sm" iconLeft={<Icon name="send" size={14} />}
                  disabled={!activeMdr || submitRegister.isPending || !isFillable(mdr?.status) || (mdr?.docs ?? 0) === 0}
                  title={!isFillable(mdr?.status) ? t(lang, 'Реестр уже подан или согласован', 'Already submitted or approved')
                          : (mdr?.docs ?? 0) === 0 ? t(lang, 'Сначала загрузите состав реестра', 'Load the register content first')
                          : t(lang, 'Реестр уйдёт заказчику трансмитталом и встанет на рассмотрение',
                                    'The register goes to the customer by transmittal and enters review')}
                  onClick={() => submitRegister.mutate({ code: activeMdr }, {
                    onSuccess: (r) => toast({ tone: 'success',
                      title: t(lang, 'Реестр подан на согласование', 'Register submitted'),
                      text: t(lang, 'Трансмиттал ' + r.id, 'Transmittal ' + r.id) }),
                  })}>
            {submitRegister.isPending ? t(lang, 'Отправка…', 'Submitting…') : t(lang, 'На согласование', 'Submit')}
          </Button>
          {/* Передаёт шаблон заказчик, и только тому подрядчику, чей это реестр:
              подрядчику своя же передача не нужна. */}
          {external ? null : (
            <Button variant="secondary" size="sm" iconLeft={<Icon name="paperclip" size={14} />}
                    disabled={!activeMdr || !mdr?.vendor || templateBusy}
                    title={!mdr?.vendor ? t(lang, 'У реестра нет подрядчика: шаблон некому передавать',
                                              'The register has no contractor to hand the template to')
                            : t(lang, 'Подрядчику уйдёт трансмиттал с файлом-шаблоном реестра',
                                      'The contractor receives a transmittal carrying the register template')}
                    onClick={handTemplate}>
              {templateBusy ? t(lang, 'Передаём…', 'Sending…') : t(lang, 'Передать шаблон', 'Hand over template')}
            </Button>
          )}
          {/* Каркас реестра заводит заказчик: подрядчик его наполняет. */}
          {external ? null : <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setCreateOpen(true)}>{t(lang, 'Создать MDR', 'New MDR')}</Button>}
        </>} />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Master rail: ширина тянется за правый край и запоминается. */}
        <ResizableRail sizeKey="rail-mdr" width={286} style={{ borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0, background: 'var(--surface-card)', zIndex: 1, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
              <span style={{ flex: 1 }}>{t(lang, 'Реестры по дисциплинам', 'Registers')}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{mdrs.length}/{mdrsQuery.data?.total ?? mdrs.length}</span>
            </div>
            {/* Поиск на сервере: искать среди первых ста — это не поиск. */}
            <Input size="sm" value={mdrSearch} onChange={(e) => setMdrSearch(e.target.value)}
                   leading={<Icon name="search" size={13} />}
                   placeholder={t(lang, 'Реестр, дисциплина, титул…', 'Register, discipline, area…')} />
          </div>
          {mdrsQuery.isPending ? <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div> : null}
          {mdrsQuery.isError ? <div style={{ padding: 14, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить реестры', 'Failed to load registers')}</div> : null}
          {mdrs.map((m) => <RailItem key={m.id} active={m.id === activeMdr} onClick={() => pickMdr(m.id)} code={m.disc} id={m.id} title={m.title} sub={m.docs + ' ' + t(lang, 'док. · отв.', 'docs · owner') + ' ' + m.owner} status={m.status} rev={m.rev} lang={lang} afd={m.afd} ifa={m.ifa} ifr={m.ifr} planned={m.planned} overdue={m.overdue} />)}
          {(mdrsQuery.data?.total ?? 0) > mdrs.length ? (
            <div style={{ padding: 10 }}>
              <Button variant="secondary" size="sm" block onClick={() => setMdrLimit((n) => n + RAIL_PAGE)}>
                {t(lang, `Показать ещё · осталось ${(mdrsQuery.data?.total ?? 0) - mdrs.length}`,
                         `Show more · ${(mdrsQuery.data?.total ?? 0) - mdrs.length} left`)}
              </Button>
            </div>
          ) : null}
          {mdrs.length === 0 && !mdrsQuery.isPending ? (
            <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {mdrSearch.trim() ? t(lang, 'Ничего не найдено', 'Nothing found') : t(lang, 'Реестров пока нет', 'No registers yet')}
            </div>
          ) : null}
        </ResizableRail>
        {/* Documents */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}><DiscTag code={mdr?.disc ?? ''} /><span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{mdr?.title ?? ''}</span></div>
              <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 2, fontFamily: 'var(--font-mono)' }}>{activeMdr} · {docsQuery.isSuccess ? `${docs.length} ${t(lang, 'из', 'of')} ${docsQuery.data.total} ${t(lang, 'документов', 'docs')}` : '—'}</div>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Ноль тут читается как «ни одного», поэтому без реестра
                  показывается прочерк, а не выдуманный ноль. */}
              <Stat n={mdr ? mdr.afd : '—'} c="--green-600" l="AFD" /><Stat n={mdr ? mdr.ifa : '—'} c="--blue-600" l="IFA" /><Stat n={mdr ? mdr.ifr : '—'} c="--accent-ink" l="IFR" /><Stat n={mdr ? mdr.overdue : '—'} c="--danger-ink" l={t(lang, 'проср.', 'ovd')} />
              <Button variant="secondary" size="sm" iconLeft={<Icon name="layout-panel-left" size={14} />} onClick={() => onOpenMdr && onOpenMdr(activeMdr)}>{t(lang, 'Карточка MDR', 'MDR card')}</Button>
            </div>
          </div>
          {/* Документ создаётся в конкретном реестре: без выбранного реестра
              команде некуда писать, поэтому кнопки просто нет. */}
          <FilterControls lang={lang} q={q} setQ={(v: string) => setFilter(() => setQ(v))} disc={disc} setDisc={(v: string) => setFilter(() => setDisc(v))} life={life} setLife={(v: string) => setFilter(() => setLife(v))} slice={slice} setSlice={(v: Slice) => setFilter(() => setSlice(v))} attrFilter={<AttrFilter lang={lang} resource="docs" defs={attrDefs} pick={attrPick} onPick={(p) => setFilter(() => setAttrPick(p))} />} attrActive={!!attrPick.code} views={<SavedFilters lang={lang} resource="docs" current={{ q, disc, life, slice }} apply={(v) => setFilter(() => { setQ(v.q); setDisc(v.disc); setLife(v.life); setSlice(v.slice) })} />} onExport={runExport} onTemplate={runTemplate} onAddDoc={activeMdr && isFillable(mdr?.status) ? () => setDocOpen(true) : undefined} />
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {docsQuery.isError
              ? <div style={{ padding: 20, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить документы', 'Failed to load documents')}</div>
              : <DocsTable name="mdr-docs" rows={docs} lang={lang} onOpenDoc={onOpenDoc} onOpenViewer={onOpenViewer} selected={selected} setSelected={setSelected} loading={docsQuery.isPending} attrs={docAttrs}
                            emptyText={docsEmpty}
                            sort={order} onSort={(o) => { setOrder(o); setPage(0) }}
                            colFilters={colFilters}
                            onFilter={(key, values) => setFilter(() => setColFilters((f) => {
                              const n = { ...f }
                              if (values && values.length) n[key] = values
                              else delete n[key]
                              return n
                            }))} />}
          </div>
          <Pager page={docsQuery.data?.page ?? 0} pageCount={docsQuery.data?.pageCount ?? 1} total={docsQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
          <BulkBar count={selected.size} total={docsQuery.data?.total} lang={lang} onClear={() => setSelected(new Set())}
                   busy={createTrm.isPending}
                   onSelectAll={selectAllFiltered}
                   onEdit={() => setBulkEditOpen(true)} onVoid={() => setBulkVoidOpen(true)}
                   onCreate={mdrVendor ? () => issueTrm(mdrVendor) : undefined}
                   createHint={t(lang, 'У реестра не назван подрядчик', 'The register has no contractor')} />
        </div>
      </div>
      <BulkVoidDialog open={bulkVoidOpen} onClose={() => setBulkVoidOpen(false)} lang={lang}
                      nos={Array.from(selected, String)} onDone={() => setSelected(new Set())} />
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} lang={lang}
                      nos={Array.from(selected, String)} onDone={() => setSelected(new Set())} />
      {/* Диалоги монтируются только открытыми: закрытая форма не должна хранить
          значения прошлого создания и держать свои запросы. Mount гаснет с
          задержкой (useExitMount) — ровно чтобы DlgShell доиграл уход. */}
      {createMount ? <MdrCreate open={createOpen} onClose={() => setCreateOpen(false)} lang={lang} onCreated={pickMdr} /> : null}
      {docMount ? <DocCreate open={docOpen} onClose={() => setDocOpen(false)} lang={lang} mdrId={activeMdr}
                             onCreated={(no) => toast({ tone: 'success', title: t(lang, 'Документ создан', 'Document created'), text: no })} /> : null}
      <ImportReport result={importResult} lang={lang} onClose={() => setImportResult(null)} />
      <BulkFilesDialog open={bulkOpen} onClose={() => setBulkOpen(false)} lang={lang} title={activeMdr}
                       load={() => collectAll().then((rows) => rows.map((d) => ({ no: d.no, rev: d.rev })))} />
    </div>)
}

export function VdrRegister({ lang = 'ru', onOpenDoc, onOpenViewer, onOpenVdr }: { lang?: Lang; onOpenDoc: (no: string) => void; onOpenViewer?: (no: string) => void; onOpenVdr?: (id: string) => void }) {
  /* Проектные атрибуты: по их заголовкам импорт узнаёт колонки файла заказчика. */
  const attrDefs = useDocAttrs()
  // Зеркало MdrRegister: заказы и документы читаются с сервера, фильтры, поиск
  // и постраничность выполняются там же.
  const [chosenVdr, setChosenVdr] = useState('')
  /* Отбор по проектной колонке — тот же, что в реестре заказчика. */
  const [attrPick, setAttrPick] = useState({ code: '', value: '' })
  const attrQuery = attrPick.code && attrPick.value ? { ['attr.' + attrPick.code]: [attrPick.value] } : {}
  const [q, setQ] = useState(''); const [disc, setDisc] = useState(''); const [life, setLife] = useState('')
  const [slice, setSlice] = useState<Slice>('')
  const [bulkVoidOpen, setBulkVoidOpen] = useState(false)
  const [page, setPage] = useState(0)
  const [selected, setSelected] = useState<Set<React.Key>>(new Set())
  const [createOpen, setCreateOpen] = useState(false)
  /* Mount переживает закрытие на длину анимации ухода — см. useExitMount. */
  const createMount = useExitMount(createOpen)

  /* Рельса заказов обрывалась на двухсотом молча: на проекте их сотни, и
     человек решал, что заказа нет вовсе, — заводил второй. У рельсы теперь
     есть поиск (на сервере) и «показать ещё». */
  const [vdrSearch, setVdrSearch] = useState('')
  const [vdrLimit, setVdrLimit] = useState(RAIL_PAGE)
  const vdrsQuery = useList<Vdr>('vdrs', {
    page: 0, pageSize: vdrLimit, sort: [{ id: 'id', desc: false }],
    q: vdrSearch.trim() || undefined,
  })
  const vdrs = vdrsQuery.data?.rows ?? []
  const vdrTotal = vdrsQuery.data?.total ?? vdrs.length
  // Раньше здесь стояло VDRS[0]! — на пустом реестре это падение при монтировании.
  const activeVdr = chosenVdr || vdrs[0]?.id || ''
  const vdr = vdrs.find((v) => v.id === activeVdr)

  /* Порядок реестра: держит экран, считает сервер (см. реестр MDR). */
  const [order, setOrder] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null)
  const sortSpec = [{ id: order?.key ?? 'no', desc: order?.dir === 'desc' }]

  /* Отбор в шапке таблицы — как в реестре MDR: применяет сервер. */
  const [colFilters, setColFilters] = useState<Record<string, string[]>>({})
  const docFilters = intersectFilters({
    vdr: [activeVdr],
    ...(disc ? { disc: [disc] } : {}),
    ...attrQuery,
    ...sliceFilters(slice, life),
  }, colFilters)

  const docsQuery = useList<Vdoc>(
    'vdocs',
    { page, pageSize: PAGE_SIZE, sort: sortSpec, filters: docFilters, q: q || undefined },
    { enabled: activeVdr !== '' },
  )
  const docs = docsQuery.data?.rows ?? []
  /* Пустой заказ объяснялся несуществующими фильтрами: «нет документов по
     заданным фильтрам» при пустом поиске и закрытой панели отбора. Оператор
     искал условие, которого нет, вместо того чтобы наполнить заказ. */
  const narrowed = Boolean(q || disc || life || slice || attrPick.code || Object.keys(colFilters).length)
  const docsEmpty = narrowed ? undefined : activeVdr ? (
    <EmptyState icon="package" title={t(lang, 'В заказе пока нет документов', 'No documents in this purchase order yet')}
                text={t(lang, 'Состав приходит трансмитталом от поставщика. Передайте ему шаблон реестра или загрузите состав файлом.',
                              'Content arrives by transmittal from the vendor. Hand over the register template or import the content from a file.')} />
  ) : (
    <EmptyState icon="package" title={t(lang, 'Заказ не выбран', 'No purchase order selected')}
                text={t(lang, 'Выберите заказ слева.', 'Pick a purchase order on the left.')} />
  )

  /* Выбор заказа только выбирает — как в реестре MDR. Открывать карточку по
     клику в списке значило бы закрывать содержимое реестра при каждой смене
     заказа; карточка открывается кнопкой в шапке. */
  /* Значения проектных колонок — одним запросом на реестр (см. реестр MDR). */
  const attrValues = useSub<{ rows: Record<string, Record<string, string>> }>(
    attrDefs.length && activeVdr ? `/vdrs/${encodeURIComponent(activeVdr)}/attributes` : null)
  const docAttrs = { defs: attrDefs, values: attrValues.data?.rows ?? {} }

  const pickVdr = (id: string) => { setChosenVdr(id); setSelected(new Set()); setPage(0) }
  const setFilter = (apply: () => void) => { apply(); setPage(0) }

  const collectAll = async (): Promise<Vdoc[]> => {
    const filters = docFilters
    const rows: Vdoc[] = []
    let p = 0
    let pageCount = 1
    do {
      const chunk = await fetchList<Vdoc>('vdocs', {
        page: p, pageSize: EXPORT_CHUNK, sort: [{ id: 'no', desc: false }], filters, q: q || undefined,
      })
      rows.push(...chunk.rows)
      pageCount = chunk.pageCount
      p += 1
    } while (p < pageCount)
    return rows
  }


  /* Выбрать весь отфильтрованный набор, а не показанную страницу. Набор
     забирается тем же обходом, что и выгрузка: сервер отдаёт страницами.

     Массовые команды принимают не больше BULK_LIMIT строк за раз — больше
     выбрать нельзя, и молчать об этом нельзя тем более: выбор выглядел бы
     полным, не будучи им. */
  const selectAllFiltered = () => {
    collectAll().then((rows) => {
      const take = rows.slice(0, BULK_LIMIT)
      setSelected(new Set(take.map((d) => d.no)))
      if (rows.length > BULK_LIMIT) {
        toast({ tone: 'info', title: t(lang, 'Выбрано не всё', 'Not everything selected'),
          text: t(lang, `${BULK_LIMIT} из ${rows.length}: за раз обрабатывается не больше ${BULK_LIMIT} строк — сузьте фильтр`,
                        `${BULK_LIMIT} of ${rows.length}: at most ${BULK_LIMIT} rows are processed at once — narrow the filter`) })
      }
    }).catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Не выбрано', 'Selection failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }
  /* Та же серверная книга, что у MDR: списки справочников и правила реестра
     собираются одним кодом — иначе шаблон поставки отстанет от шаблона MDR. */
  const exportAll = async () => {
    await saveFile(`/vdrs/${encodeURIComponent(activeVdr)}/book?rows=all`)
    noteExport('реестр VDR ' + activeVdr, undefined, vdr?.docs)
  }
  const downloadTemplate = async () => {
    await saveFile(`/vdrs/${encodeURIComponent(activeVdr)}/book?rows=none`)
    noteExport('шаблон реестра VDR ' + activeVdr)
  }

  const runTemplate = () => {
    downloadTemplate().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Шаблон не собран', 'Template failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }
  const runExport = () => {
    exportAll().catch((e: unknown) => {
      toast({ tone: 'danger', title: t(lang, 'Выгрузка не удалась', 'Export failed'),
        text: e instanceof Error ? e.message : String(e) })
    })
  }

  const discLabels = useDictLabels('disc')
  const project = useMe().data?.project ?? ''
  /* Импорт реестра поставщика — тот же путь, что у MDR: файл разбирается здесь,
     строки проверяет сервер, отчёт приходит построчный. Поставщика из файла не
     берём вовсе: реестр заведён под заказ конкретной организации, и сервер
     подставляет её сам. */
  const vdocFileRef = useRef<HTMLInputElement>(null)
  const vdocCheckRef = useRef<HTMLInputElement>(null)
  const [vdocImportResult, setVdocImportResult] = useState<ImportResult | null>(null)
  const importVdocs = useCommand<{ rows: DocRow[] }, ImportResult>({
    path: () => `/vdrs/${encodeURIComponent(activeVdr)}/docs:import`,
    body: (a) => ({ rows: a.rows }),
    invalidate: () => [qk.all('vdrs'), qk.all('vdocs'), qk.all('docs'), qk.all('audit')],
  })
  // Сухой прогон — как у MDR: те же правила, ничего не создаётся.
  const checkVdocs = useCommand<{ rows: DocRow[] }, ImportResult>({
    path: () => `/vdrs/${encodeURIComponent(activeVdr)}/docs:validate`,
    body: (a) => ({ rows: a.rows }),
    invalidate: () => [],
  })
  const runVdocFile = (file: File, dry: boolean) => {
    parseDocs(file)
      .then((sheet) => {
        const cmd = dry ? checkVdocs : importVdocs
        // Колонки, не легшие никуда, называются вслух — как в реестре MDR.
        const lost = unknownColumns(sampleRow(sheet.rows), attrDefs)
        if (lost.length) {
          toast({ tone: 'info', title: t(lang, 'Колонки файла не разобраны', 'Unmapped columns'),
            text: lost.slice(0, 6).join(', ') })
        }
        cmd.mutate({ rows: sheet.rows.map((r) => toDocRow(r, attrDefs)) },
          { onSuccess: (r) => setVdocImportResult(onFileLines(r, sheet.lines)) })
      })
      .catch((e: unknown) => {
        toast({ tone: 'danger', title: t(lang, 'Файл не прочитан', 'Cannot read the file'),
          text: e instanceof Error ? e.message : String(e) })
      })
  }

  /* Шаг 6 регламента у заказа на поставку — тот же, что у MDR: поставщику
     уходит пакет с файлом-шаблоном и без состава документов. */
  const [templateBusy, setTemplateBusy] = useState(false)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [bulkEditOpen, setBulkEditOpen] = useState(false)
  const sendTemplate = useCommand<{ recipient: string; registerCode: string }, { id: string }>({
    path: () => '/transmittals',
    body: (a) => ({ purpose: 'IFI', recipient: a.recipient, docNos: [], registerCode: a.registerCode }),
    invalidate: () => [qk.all('transmittals'), qk.all('audit'), ...inboxKeys],
  })
  const handTemplate = () => {
    const recipient = vdr?.vendor
    if (!activeVdr || !recipient) return
    setTemplateBusy(true)
    sendTemplate.mutate({ recipient, registerCode: activeVdr }, {
      onSuccess: (r) => {
        /* Та же серверная книга, что у MDR: два способа собрать шаблон однажды
           разошлись бы, и подрядчик поставки заполнял бы файл другого вида. */
        requestFile(`/vdrs/${encodeURIComponent(activeVdr)}/book?rows=none`)
          .then(({ blob, fileName }) => uploadTrmFile(r.id, new File([blob], fileName,
            { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'other'))
          .then(() => toast({ tone: 'success', title: t(lang, 'Шаблон передан', 'Template handed over'),
            text: r.id + ' · ' + recipient }))
          /* Повторное нажатие завело бы второй пакет, а файл нужен этому:
             говорим, куда идти, — в карточке пакета есть «Приложить файл». */
          .catch((e: unknown) => toast({ tone: 'danger',
            title: t(lang, 'Пакет создан, файл не ушёл', 'Package created, file not uploaded'),
            text: r.id + ' · ' + (e instanceof Error ? e.message : String(e)) + ' · '
              + t(lang, 'приложите шаблон в карточке пакета, не создавая новый',
                        'attach the template in the package card instead of creating a new one') }))
          .finally(() => setTemplateBusy(false))
      },
      onError: (e: unknown) => {
        setTemplateBusy(false)
        toast({ tone: 'danger', title: t(lang, 'Шаблон не передан', 'Handover failed'),
          text: e instanceof Error ? e.message : String(e) })
      },
    })
  }

  /* Выпуск исходящего трансмиттала. Получатель — поставщик заказа, назначение
     IFR: с реестра поставщика документы уходят на рассмотрение. Номер, срок и
     состав считает сервер, поэтому после успеха просто перечитываем. */
  const createTrm = useCommand<{ recipient: string; docNos: string[] }, { id: string; status: string; docs: number }>({
    path: () => '/transmittals',
    body: (a) => ({ purpose: 'IFR', recipient: a.recipient, docNos: a.docNos }),
    invalidate: () => [qk.all('transmittals'), qk.all('vdocs'), qk.all('docs'), qk.all('audit')],
  })
  const issueTrm = (recipient: string) => createTrm.mutate(
    { recipient, docNos: Array.from(selected, String) },
    {
      onSuccess: (r) => {
        // Выбор снимается только после ответа: до него документы ещё не в TRM.
        setSelected(new Set())
        toast({ tone: 'success', title: t(lang, 'Трансмиттал выпущен', 'Transmittal issued'), text: r.id + ' · ' + r.docs + ' ' + t(lang, 'док.', 'docs') })
      },
    },
  )

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--surface-page)' }}>
      <PageHead
        crumbs={<span>{project} <span style={{ color: 'var(--border-strong)' }}>/</span> {t(lang, 'Реестр VDR', 'VDR register')}</span>}
        title={t(lang, 'Реестр VDR / заказы на поставку', 'VDR register / Purchase Orders')} count={vdrsQuery.isSuccess ? vdrsQuery.data.total + ' PO' : '—'}
        actions={<>
          <input ref={vdocFileRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) runVdocFile(f, false); e.target.value = '' }} />
          <input ref={vdocCheckRef} type="file" accept=".xlsx" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) runVdocFile(f, true); e.target.value = '' }} />
          <Button variant="secondary" size="sm" iconLeft={<Icon name="scan-search" size={14} />}
                  disabled={!activeVdr || checkVdocs.isPending}
                  title={t(lang, 'Прогнать файл по правилам загрузки, ничего не создавая',
                                'Run the file through the import rules without creating anything')}
                  onClick={() => vdocCheckRef.current?.click()}>{checkVdocs.isPending ? t(lang, 'Проверка…', 'Checking…') : t(lang, 'Проверить файл', 'Check file')}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="paperclip" size={14} />}
                  disabled={!activeVdr}
                  title={t(lang, 'Разнести файлы по строкам реестра по номеру в имени',
                                'Spread files across register rows by the number in the file name')}
                  onClick={() => setBulkOpen(true)}>{t(lang, 'Файлы пакетом', 'Bulk files')}</Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="upload" size={14} />}
                  disabled={!activeVdr || importVdocs.isPending}
                  title={activeVdr ? undefined : t(lang, 'Сначала выберите заказ', 'Select a purchase order first')}
                  onClick={() => vdocFileRef.current?.click()}>
            {importVdocs.isPending ? t(lang, 'Импорт…', 'Importing…') : t(lang, 'Импорт', 'Import')}
          </Button>
          <Button variant="secondary" size="sm" iconLeft={<Icon name="paperclip" size={14} />}
                  disabled={!activeVdr || !vdr?.vendor || templateBusy}
                  title={!vdr?.vendor ? t(lang, 'У заказа нет поставщика: шаблон некому передавать',
                                            'The purchase order has no vendor to hand the template to')
                          : t(lang, 'Поставщику уйдёт трансмиттал с файлом-шаблоном реестра',
                                    'The vendor receives a transmittal carrying the register template')}
                  onClick={handTemplate}>
            {templateBusy ? t(lang, 'Передаём…', 'Sending…') : t(lang, 'Передать шаблон', 'Hand over template')}
          </Button>
          <Button variant="primary" size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => setCreateOpen(true)}>{t(lang, 'Создать VDR под PO', 'New VDR')}</Button>
        </>} />

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Рельса заказов: ширина тянется за правый край и запоминается. */}
        <ResizableRail sizeKey="rail-vdr" width={300} style={{ borderRight: '1px solid var(--border-default)', background: 'var(--surface-card)', overflow: 'auto' }}>
          <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--border-subtle)', position: 'sticky', top: 0, background: 'var(--surface-card)', zIndex: 1, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>
              <span style={{ flex: 1 }}>{t(lang, 'Заказы на поставку', 'Purchase orders')}</span>
              <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>{vdrs.length}/{vdrTotal}</span>
            </div>
            {/* Поиск идёт на сервере: фильтровать загруженную страницу значит
                искать среди первых ста. */}
            <Input size="sm" value={vdrSearch} onChange={(e) => setVdrSearch(e.target.value)}
                   leading={<Icon name="search" size={13} />}
                   placeholder={t(lang, 'Заказ, поставщик, предмет…', 'PO, vendor, subject…')} />
          </div>
          {vdrsQuery.isPending ? <div style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 8, font: 'var(--font-ui)', color: 'var(--text-subtle)' }}><Spinner size={13} />{t(lang, 'Загрузка…', 'Loading…')}</div> : null}
          {vdrsQuery.isError ? <div style={{ padding: 14, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить заказы', 'Failed to load purchase orders')}</div> : null}
          {vdrs.map((v) =>
            <button key={v.id} onClick={() => pickVdr(v.id)} className="tdms-rail" style={{ display: 'block', width: '100%', textAlign: 'left', padding: '11px 12px', border: 0, borderBottom: '1px solid var(--border-subtle)', background: v.id === activeVdr ? 'var(--accent-soft)' : 'transparent', boxShadow: v.id === activeVdr ? 'inset 2px 0 0 var(--accent)' : 'none', cursor: 'pointer' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="package" size={13} />
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-strong)' }}>{v.po}</span>
                {v.overdue > 0 ? <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--danger-ink)' }}>● {v.overdue}</span> : null}
              </div>
              <div style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)', marginTop: 3 }}>{v.vendor}</div>
              <div style={{ font: 'var(--font-caption)', color: 'var(--text-subtle)', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.subject}</div>
              <div style={{ marginTop: 7 }}><CompletionBar afd={v.afd} ifa={v.ifa} ifr={v.ifr} height={5} /></div>
            </button>
          )}
          {vdrTotal > vdrs.length ? (
            <div style={{ padding: 10 }}>
              <Button variant="secondary" size="sm" block onClick={() => setVdrLimit((n) => n + RAIL_PAGE)}>
                {t(lang, `Показать ещё · осталось ${vdrTotal - vdrs.length}`, `Show more · ${vdrTotal - vdrs.length} left`)}
              </Button>
            </div>
          ) : null}
          {vdrs.length === 0 && !vdrsQuery.isPending ? (
            <div style={{ padding: 14, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
              {vdrSearch.trim() ? t(lang, 'Ничего не найдено', 'Nothing found') : t(lang, 'Заказов пока нет', 'No purchase orders yet')}
            </div>
          ) : null}
        </ResizableRail>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          {/* PO header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <span style={{ font: 'var(--font-card-title)', color: 'var(--text-strong)' }}>{vdr?.subject ?? ''}</span>
              <Badge tone="accent" size="sm">{vdr?.po ?? ''}</Badge>
              <Button variant="secondary" size="sm" style={{ marginLeft: 'auto' }} disabled={!activeVdr}
                      iconLeft={<Icon name="layout-panel-left" size={14} />}
                      onClick={() => onOpenVdr && onOpenVdr(activeVdr)}>{t(lang, 'Карточка VDR', 'VDR card')}</Button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              <Field label={t(lang, 'Поставщик', 'Vendor')}>{vdr?.vendor ?? ''}</Field>
              <Field label={t(lang, 'Дисциплина', 'Discipline')}><DiscTag code={vdr?.disc ?? ''} name={vdr ? discLabels[vdr.disc] : undefined} /></Field>
              <Field label={t(lang, 'Документов', 'Documents')} mono>{vdr?.docs ?? 0}</Field>
              <Field label={t(lang, 'Срок выпуска', 'Due')} mono>{vdr?.due ?? '—'}</Field>
              <Field label={t(lang, 'Просрочено', 'Overdue')}><span style={{ color: vdr?.overdue ? 'var(--danger-ink)' : 'var(--text-strong)', fontFamily: 'var(--font-mono)' }}>{vdr?.overdue ?? 0}</span></Field>
            </div>
          </div>
          <FilterControls lang={lang} q={q} setQ={(v: string) => setFilter(() => setQ(v))} disc={disc} setDisc={(v: string) => setFilter(() => setDisc(v))} life={life} setLife={(v: string) => setFilter(() => setLife(v))} slice={slice} setSlice={(v: Slice) => setFilter(() => setSlice(v))} attrFilter={<AttrFilter lang={lang} resource="vdocs" defs={attrDefs} pick={attrPick} onPick={(p) => setFilter(() => setAttrPick(p))} />} attrActive={!!attrPick.code} views={<SavedFilters lang={lang} resource="vdocs" current={{ q, disc, life, slice }} apply={(v) => setFilter(() => { setQ(v.q); setDisc(v.disc); setLife(v.life); setSlice(v.slice) })} />} onExport={runExport} onTemplate={runTemplate} />
          <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
            {docsQuery.isError
              ? <div style={{ padding: 20, font: 'var(--font-ui)', color: 'var(--danger-ink)' }}>{t(lang, 'Не удалось загрузить документы', 'Failed to load documents')}</div>
              : <DocsTable name="vdr-docs" resource="vdocs" rows={docs} lang={lang} onOpenDoc={onOpenDoc} onOpenViewer={onOpenViewer} selected={selected} setSelected={setSelected} loading={docsQuery.isPending} attrs={docAttrs}
                            emptyText={docsEmpty}
                            sort={order} onSort={(o) => { setOrder(o); setPage(0) }}
                            colFilters={colFilters}
                            onFilter={(key, values) => setFilter(() => setColFilters((f) => {
                              const n = { ...f }
                              if (values && values.length) n[key] = values
                              else delete n[key]
                              return n
                            }))} />}
          </div>
          <Pager page={docsQuery.data?.page ?? 0} pageCount={docsQuery.data?.pageCount ?? 1} total={docsQuery.data?.total ?? 0} lang={lang} onPage={setPage} />
          <BulkBar count={selected.size} total={docsQuery.data?.total} lang={lang} onClear={() => setSelected(new Set())} busy={createTrm.isPending}
                   onSelectAll={selectAllFiltered}
                   onEdit={() => setBulkEditOpen(true)} onVoid={() => setBulkVoidOpen(true)}
                   onCreate={vdr ? () => issueTrm(vdr.vendor) : undefined} />
        </div>
      </div>
      {/* Итог импорта показывается целиком и всегда — как у реестра MDR:
          отклонённые строки надо видеть построчно, а не догадываться по числу
          появившихся документов. */}
      <ImportReport result={vdocImportResult} lang={lang} onClose={() => setVdocImportResult(null)} />
      <BulkFilesDialog open={bulkOpen} onClose={() => setBulkOpen(false)} lang={lang} title={activeVdr}
                       load={() => collectAll().then((rows) => rows.map((d) => ({ no: d.no, rev: d.rev })))} />
      <BulkVoidDialog open={bulkVoidOpen} onClose={() => setBulkVoidOpen(false)} lang={lang}
                      nos={Array.from(selected, String)} onDone={() => setSelected(new Set())} />
      <BulkEditDialog open={bulkEditOpen} onClose={() => setBulkEditOpen(false)} lang={lang}
                      nos={Array.from(selected, String)} onDone={() => setSelected(new Set())} />
      {/* Новый реестр становится активным, но экран не уводим: карточку заказа
          человек откроет сам, если она ему нужна. */}
      {createMount ? <VdrCreate open={createOpen} onClose={() => setCreateOpen(false)} lang={lang}
                                onCreated={(id) => { setChosenVdr(id); setSelected(new Set()); setPage(0) }} /> : null}
    </div>)


}
function Stat({ n, c, l }: { n: ReactNode; c: string; l: ReactNode }) {
  return <div style={{ textAlign: 'right' }}><div style={{ fontFamily: 'var(--font-mono)', fontSize: 15, fontWeight: 600, color: `var(${c})` }}>{n}</div><div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)' }}>{l}</div></div>
}
